import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExperimentOutlined,
  ExportOutlined,
  ImportOutlined,
  MoreOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SendOutlined,
  StopOutlined,
  SwapOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  AutoComplete,
  Button,
  Cascader,
  Checkbox,
  DatePicker,
  Descriptions,
  Dropdown,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Col,
  Result,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadFile } from 'antd';
import {
  ProCard,
  ProFormDateRangePicker,
  ProFormSelect,
  ProFormText,
  ProFormTreeSelect,
  ProTable,
  QueryFilter,
  type ActionType,
  type ProColumns,
  type ProFormInstance,
} from '@ant-design/pro-components';
import type { CurrentUser } from '@cell-bank/shared';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatTenantDate, formatTenantDateTime } from '../lib/tenant';

type Sample = {
  id: string;
  sampleNo: string;
  barcode: string | null;
  cellLineId: string;
  cellMaterialId?: string | null;
  materialName?: string | null;
  materialType?: MaterialType | null;
  passage?: string | null;
  status: string;
  cellCount: string | null;
  storagePositionId: string | null;
  reservedByRequestId?: string | null;
  qcStatus?: string;
  freezeDate?: string | null;
  expiryDate?: string | null;
  customFields?: Record<string, unknown> | null;
  version: number;
};
type SampleListResponse = {
  items: Sample[];
  statusCounts?: Record<string, number>;
  operators?: { id: string; username: string }[];
};
type CellLine = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
};
type CellLineCatalogEntry = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  species: string;
  riskStatus: 'CLEAR' | 'REVIEW' | 'MISIDENTIFIED' | 'CONTAMINATION_REPORTED';
  enabled: boolean;
  cellLineId: string | null;
};
type MaterialType =
  | 'PARENTAL'
  | 'TRANSIENT_POOL'
  | 'STABLE_POOL'
  | 'CLONE'
  | 'ENGINEERED_LINE'
  | 'MIXED_POPULATION'
  | 'OTHER';
type ModificationType =
  | 'KI'
  | 'TAG'
  | 'KO'
  | 'OE'
  | 'KD'
  | 'REPORTER'
  | 'OTHER';
type TagPosition = 'N_TERMINAL' | 'C_TERMINAL' | 'INTERNAL' | 'UNSPECIFIED';
type ModificationStrategy =
  | 'ENDOGENOUS_KI'
  | 'EXOGENOUS_OE'
  | 'STABLE_INTEGRATION'
  | 'TRANSIENT_EXPRESSION'
  | 'OTHER';
type ValidationStatus = 'UNVERIFIED' | 'SCREENING' | 'VERIFIED';
type CellMaterial = {
  id: string;
  cellLineId: string;
  cellLineCode: string;
  parentMaterialId: string | null;
  type: MaterialType;
  name: string;
  modificationType: ModificationType | null;
  targetGene: string | null;
  tagName: string | null;
  tagPosition: TagPosition | null;
  modificationStrategy: ModificationStrategy | null;
  constructName: string | null;
  selectionMarker: string | null;
  cloneNo: string | null;
  validationStatus: ValidationStatus;
  notes: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  version: number;
};
type CellMaterialForm = {
  cellLineId: string;
  parentMaterialId?: string;
  type: MaterialType;
  name: string;
  modificationType?: ModificationType;
  targetGene?: string;
  tagName?: string;
  tagPosition?: TagPosition;
  modificationStrategy?: ModificationStrategy;
  constructName?: string;
  selectionMarker?: string;
  cloneNo?: string;
  validationStatus: ValidationStatus;
  notes?: string;
};
type OriginType = 'COMMERCIAL' | 'IN_HOUSE' | 'DERIVED' | 'OTHER';
type CellLineForm = {
  code: string;
  species: string;
  originType: OriginType;
  source?: string;
  catalogNo?: string;
  description?: string;
};
type Position = {
  id: string;
  path: string;
  displayPath?: string;
  parentId?: string | null;
  type?: string;
  depth?: number;
};
type ImportJob = {
  id: string;
  importNo?: string;
  status: 'PRECHECKED' | 'EXECUTING' | 'COMPLETED' | 'FAILED';
  objectType: 'SAMPLE' | 'CELL_LINE';
  sourceFormat: 'XLSX' | 'CSV';
  originalFilename?: string;
  totalRows: number;
  validRows: number;
  errorRows: number;
  autoEnableRows?: number;
  version: number;
  executedAt?: string | null;
  errors: {
    rowNumber: number;
    field?: string | null;
    code: string;
    messageKey: string;
  }[];
};
type SampleForm = {
  sampleNo?: string;
  barcode?: string;
  cellLineId: string;
  cellMaterialId?: string;
  cellCount?: string;
  passage?: string;
  label?: string;
  ownerName?: string;
  notes?: string;
  putawayReason?: string;
  storagePositionPath?: string[];
  freezeDate?: Dayjs;
  expiryDate?: Dayjs;
  enableQc?: boolean;
  qcTemplateId?: string;
  qcDefinitionIds?: string[];
  qcTestedAt?: string;
  qcNotes?: string;
  values?: Record<string, string | number | boolean | undefined>;
  qcAttachment?: UploadFile[];
};
type SampleAction = 'move' | 'bulkMove' | 'transition' | 'correct';
type ActionForm = {
  status?: string;
  storagePositionId?: string;
  toLocationId?: string;
  reason: string;
};
type QcResult = {
  id: string;
  templateCode: string | null;
  testedAt: string;
  conclusion: string;
  notes?: string | null;
  items: {
    definitionId: string;
    definitionCode: string;
    value: unknown;
    passed: boolean | null;
    abnormalReason: string | null;
  }[];
  attachments: {
    id: string;
    originalFilename: string;
    mimeType: string;
    byteSize: number;
    sha256: string;
  }[];
};
type QcTemplateItem = {
  definitionId: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  dataType: 'TEXT' | 'NUMBER' | 'BOOLEAN' | 'SELECT';
  unit: string | null;
  isRequired: boolean;
  options?: string[];
};
type QcDefinition = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  dataType: 'TEXT' | 'NUMBER' | 'BOOLEAN' | 'SELECT';
  unit: string | null;
  isRequired: boolean;
  options?: string[];
};
type QcTemplate = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  items: QcTemplateItem[];
};
type BatchQcForm = {
  templateId?: string;
  definitionIds?: string[];
  testedAt: string;
  notes?: string;
  values: Record<string, unknown>;
  attachment?: UploadFile[];
};
type Transaction = {
  id: string;
  transactionNo: string;
  operationType: string;
  executedAt: string;
  items: { sampleId: string; fromStatus: string | null; toStatus: string }[];
};
type BulkMovePreviewIssue = {
  sampleId: string | null;
  code: string;
  message: string;
};
type BulkMovePreviewItem = {
  sampleId: string | null;
  sampleNo: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  fromLocationId: string | null;
  toLocationId: string | null;
  version: number | null;
  requestedVersion: number | null;
  issues: BulkMovePreviewIssue[];
};
type BulkMovePreview = {
  executable: boolean;
  itemCount: number;
  items: BulkMovePreviewItem[];
  issues: BulkMovePreviewIssue[];
};
type BulkMoveRequest = {
  reason: string;
  items: {
    sampleId: string;
    version: number;
    toLocationId: string | undefined;
  }[];
};

type RequestOperation =
  | 'PUTAWAY'
  | 'CONSUME'
  | 'LOAN'
  | 'RETURN'
  | 'MOVE'
  | 'DISPOSE'
  | 'QUARANTINE';
type RequestForm = {
  title?: string;
  reason?: string;
  toLocationIds?: Record<string, string>;
};
type CreatedRequest = {
  id: string;
  requestNo: string;
  operationType: RequestOperation;
  status: string;
  version: number;
};
type OperationResult =
  | {
      kind: 'request';
      request: CreatedRequest;
      submitFailed?: boolean;
    }
  | {
      kind: 'execution';
      transactionId: string;
      transactionNo: string;
      operationType: RequestOperation;
      itemCount: number;
    };
type OperationSettings = Record<
  string,
  { enabled: boolean; userVisible: boolean }
>;

type SampleFilterState = {
  query: string;
  status?: string | undefined;
  storageLocationId?: string | undefined;
  cellLineId?: string | undefined;
  operatorId?: string | undefined;
  cellMaterialId?: string | undefined;
  qcStatus?: string | undefined;
  freezeDateFrom?: string | undefined;
  freezeDateTo?: string | undefined;
  expiryDateFrom?: string | undefined;
  expiryDateTo?: string | undefined;
};

type SampleQueryForm = {
  query?: string;
  storageLocationId?: string;
  cellLineId?: string;
  operatorId?: string;
  status?: string;
  cellMaterialId?: string;
  qcStatus?: string;
  freezeDateRange?: [Dayjs, Dayjs];
  expiryDateRange?: [Dayjs, Dayjs];
};

function lastStringValue(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined;
  const last = (value as unknown[]).at(-1);
  return typeof last === 'string' ? last : undefined;
}

const SAMPLE_STATUSES = [
  'AVAILABLE',
  'RESERVED',
  'LOANED',
  'CONSUMED',
  'DISPOSED',
  'QUARANTINED',
  'MISSING',
] as const;
const ALL_SAMPLE_STATUSES = '__ALL__';
const CHANGEABLE_SAMPLE_STATUSES = [
  'AVAILABLE',
  'QUARANTINED',
  'MISSING',
] as const;
const REQUEST_OPERATIONS: RequestOperation[] = [
  'CONSUME',
  'LOAN',
  'RETURN',
  'MOVE',
  'DISPOSE',
  'QUARANTINE',
];
const DEFAULT_OPERATION_SETTINGS: OperationSettings = {
  PUTAWAY: { enabled: true, userVisible: true },
  CONSUME: { enabled: true, userVisible: true },
  LOAN: { enabled: false, userVisible: false },
  RETURN: { enabled: false, userVisible: false },
  MOVE: { enabled: true, userVisible: false },
  DISPOSE: { enabled: true, userVisible: false },
  QUARANTINE: { enabled: false, userVisible: false },
};

function requestOperationIcon(operation: RequestOperation) {
  switch (operation) {
    case 'PUTAWAY':
      return <ImportOutlined />;
    case 'CONSUME':
      return <ExportOutlined />;
    case 'LOAN':
      return <SendOutlined />;
    case 'RETURN':
      return <ImportOutlined />;
    case 'MOVE':
      return <SwapOutlined />;
    case 'DISPOSE':
      return <DeleteOutlined />;
    case 'QUARANTINE':
      return <StopOutlined />;
  }
}

function expectedStatusesForRequest(operation: RequestOperation) {
  if (operation === 'PUTAWAY') return ['DRAFT'];
  if (operation === 'RETURN') return ['LOANED'];
  if (operation === 'DISPOSE') return ['AVAILABLE', 'QUARANTINED'];
  return ['AVAILABLE'];
}

function countSampleStatuses(samples: Sample[]): Record<string, number> {
  return samples.reduce<Record<string, number>>((counts, sample) => {
    counts[sample.status] = (counts[sample.status] ?? 0) + 1;
    return counts;
  }, {});
}

function uploadFileList(event: { fileList: UploadFile[] }) {
  return event.fileList;
}

export function SamplesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState<Sample[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [currentUser, setCurrentUser] = useState<CurrentUser>();
  const [query, setQuery] = useState(
    () => new URLSearchParams(location.search).get('query') ?? '',
  );
  const [status, setStatus] = useState<string | undefined>(
    () => new URLSearchParams(location.search).get('status') ?? undefined,
  );
  const [activeFilters, setActiveFilters] = useState<
    Partial<SampleFilterState>
  >(() => ({
    query: new URLSearchParams(location.search).get('query') ?? '',
    status: new URLSearchParams(location.search).get('status') ?? undefined,
    operatorId:
      new URLSearchParams(location.search).get('operatorId') ?? undefined,
  }));
  const [storageLocationId, setStorageLocationId] = useState<string>();
  const [cellLineId, setCellLineId] = useState<string>();
  const [operatorId, setOperatorId] = useState<string | undefined>(
    () => new URLSearchParams(location.search).get('operatorId') ?? undefined,
  );
  const [cellMaterialId, setCellMaterialId] = useState<string>();
  const [qcStatus, setQcStatus] = useState<string>();
  const [freezeDateRange, setFreezeDateRange] = useState<[Dayjs, Dayjs]>();
  const [expiryDateRange, setExpiryDateRange] = useState<[Dayjs, Dayjs]>();
  const [operators, setOperators] = useState<
    { id: string; username: string }[]
  >([]);
  const [lines, setLines] = useState<CellLine[]>([]);
  const [materials, setMaterials] = useState<CellMaterial[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [allPositions, setAllPositions] = useState<Position[]>([]);
  const [storageLocations, setStorageLocations] = useState<Position[]>([]);
  const [selected, setSelected] = useState<Sample>();
  const [qcResults, setQcResults] = useState<QcResult[]>([]);
  const [definitions, setDefinitions] = useState<QcDefinition[]>([]);
  const [templates, setTemplates] = useState<QcTemplate[]>([]);
  const [templatesEnabled, setTemplatesEnabled] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Sample>();
  const [cellLineOpen, setCellLineOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogEntries, setCatalogEntries] = useState<CellLineCatalogEntry[]>(
    [],
  );
  const [materialOpen, setMaterialOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [action, setAction] = useState<{
    type: SampleAction;
    sample?: Sample;
    transaction?: Transaction;
  }>();
  const [batchQcOpen, setBatchQcOpen] = useState(false);
  const [singleQcSample, setSingleQcSample] = useState<Sample>();
  const [bulkMovePreview, setBulkMovePreview] = useState<BulkMovePreview>();
  const [pendingBulkMove, setPendingBulkMove] = useState<BulkMoveRequest>();
  const [bulkMoveTargets, setBulkMoveTargets] = useState<
    Record<string, string>
  >({});
  const [bulkMoveBoxPath, setBulkMoveBoxPath] = useState<string>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestOperation, setRequestOperation] =
    useState<RequestOperation>('CONSUME');
  const [requestSelection, setRequestSelection] = useState<Sample[]>([]);
  const [requestResult, setRequestResult] = useState<OperationResult>();
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [file, setFile] = useState<UploadFile>();
  const [job, setJob] = useState<ImportJob>();
  const [form] = Form.useForm<SampleForm>();
  const [cellLineForm] = Form.useForm<CellLineForm>();
  const [materialForm] = Form.useForm<CellMaterialForm>();
  const [actionForm] = Form.useForm<ActionForm>();
  const [batchQcForm] = Form.useForm<BatchQcForm>();
  const [requestForm] = Form.useForm<RequestForm>();
  const actionRef = useRef<ActionType | undefined>(undefined);
  const queryFormRef = useRef<ProFormInstance<SampleQueryForm> | undefined>(
    undefined,
  );
  const [messageApi, holder] = message.useMessage();
  const canManageImports =
    currentUser?.permissions.includes('import:manage') ?? false;
  const canManageQc = currentUser?.permissions.includes('qc:manage') ?? false;
  const canCreateSamples =
    currentUser?.permissions.includes('sample:create') ?? false;
  const canEditSamples =
    currentUser?.permissions.includes('sample:update') ?? false;
  const canExecuteInventory =
    currentUser?.permissions.includes('inventory:execute') ?? false;
  const canRequestInventory =
    currentUser?.permissions.includes('inventory:request') ?? false;
  const selectedTemplateId = Form.useWatch('templateId', batchQcForm);
  const selectedDefinitionIds = Form.useWatch('definitionIds', batchQcForm);
  const selectedCreateQcTemplateId = Form.useWatch('qcTemplateId', form);
  const selectedCreateQcDefinitionIds = Form.useWatch('qcDefinitionIds', form);
  const createQcEnabled = Form.useWatch('enableQc', form);
  const selectedCellLineId = Form.useWatch('cellLineId', form) as
    | string
    | undefined;
  const selectedMaterialModificationType = Form.useWatch(
    'modificationType',
    materialForm,
  );
  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId),
    [templates, selectedTemplateId],
  );
  const selectedCreateQcTemplate = useMemo(
    () =>
      templates.find((template) => template.id === selectedCreateQcTemplateId),
    [templates, selectedCreateQcTemplateId],
  );
  const selectedQcItems = useMemo<QcTemplateItem[]>(
    () =>
      templatesEnabled
        ? (selectedTemplate?.items ?? [])
        : definitions
            .filter((definition) =>
              selectedDefinitionIds?.includes(definition.id),
            )
            .map((definition) => ({
              ...definition,
              definitionId: definition.id,
            })),
    [definitions, selectedDefinitionIds, selectedTemplate, templatesEnabled],
  );
  const selectedCreateQcItems = useMemo<QcTemplateItem[]>(
    () =>
      templatesEnabled
        ? (selectedCreateQcTemplate?.items ?? [])
        : definitions
            .filter((definition) =>
              selectedCreateQcDefinitionIds?.includes(definition.id),
            )
            .map((definition) => ({
              ...definition,
              definitionId: definition.id,
            })),
    [
      definitions,
      selectedCreateQcDefinitionIds,
      selectedCreateQcTemplate,
      templatesEnabled,
    ],
  );
  const formatDate = (value?: Dayjs) => value?.format('YYYY-MM-DD');
  const dateValue = (value?: string | null) => {
    const date = formatTenantDate(value);
    return date === '-' ? undefined : dayjs(date);
  };
  const ownerName = (sample?: Pick<Sample, 'customFields'>) => {
    const value = sample?.customFields?.ownerName;
    return typeof value === 'string' ? value : undefined;
  };
  const sampleLabel = (sample?: Pick<Sample, 'customFields'>) => {
    const value = sample?.customFields?.label;
    return typeof value === 'string' ? value : undefined;
  };
  const sampleNotes = (sample?: Pick<Sample, 'customFields'>) => {
    const value = sample?.customFields?.notes;
    return typeof value === 'string' ? value : undefined;
  };
  const originTypeOptions = (
    ['COMMERCIAL', 'IN_HOUSE', 'DERIVED', 'OTHER'] as OriginType[]
  ).map((value) => ({
    value,
    label: t(`cellLines.originTypeOptions.${value}`),
  }));
  const speciesOptions = [
    'Homo sapiens',
    'Mus musculus',
    'Rattus norvegicus',
    'Cricetulus griseus',
    'Chlorocebus sabaeus',
    'Mesocricetus auratus',
  ].map((value) => ({ value }));
  const materialTypeOptions = (
    [
      'PARENTAL',
      'TRANSIENT_POOL',
      'STABLE_POOL',
      'CLONE',
      'ENGINEERED_LINE',
      'MIXED_POPULATION',
      'OTHER',
    ] as MaterialType[]
  ).map((value) => ({
    value,
    label: t(`samples.materialTypeOptions.${value}`),
  }));
  const modificationTypeOptions = (
    ['KI', 'TAG', 'KO', 'OE', 'KD', 'REPORTER', 'OTHER'] as ModificationType[]
  ).map((value) => ({
    value,
    label: t(`samples.modificationTypeOptions.${value}`),
  }));
  const tagPositionOptions = (
    ['N_TERMINAL', 'C_TERMINAL', 'INTERNAL', 'UNSPECIFIED'] as TagPosition[]
  ).map((value) => ({
    value,
    label: t(`samples.tagPositionOptions.${value}`),
  }));
  const modificationStrategyOptions = (
    [
      'ENDOGENOUS_KI',
      'EXOGENOUS_OE',
      'STABLE_INTEGRATION',
      'TRANSIENT_EXPRESSION',
      'OTHER',
    ] as ModificationStrategy[]
  ).map((value) => ({
    value,
    label: t(`samples.modificationStrategyOptions.${value}`),
  }));
  const validationStatusOptions = (
    ['UNVERIFIED', 'SCREENING', 'VERIFIED'] as ValidationStatus[]
  ).map((value) => ({
    value,
    label: t(`samples.validationStatusOptions.${value}`),
  }));
  const materialLabel = (material?: CellMaterial | null) =>
    material
      ? `${material.name} · ${t(`samples.materialTypeOptions.${material.type}`)}`
      : '-';
  const helpLabel = (label: string, help: string) => (
    <Space size={4} align="center">
      <span>{label}</span>
      <Tooltip title={help}>
        <QuestionCircleOutlined
          aria-label={help}
          style={{ color: '#8c8c8c' }}
        />
      </Tooltip>
    </Space>
  );
  const materialsForLine = (cellLineId?: string) =>
    materials.filter((material) => material.cellLineId === cellLineId);
  const defaultMaterialId = (cellLineId?: string) =>
    materialsForLine(cellLineId).find(
      (material) => material.type === 'PARENTAL',
    )?.id;
  const loadCatalog = async (query = catalogQuery) => {
    setCatalogLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (query.trim()) params.set('query', query.trim());
      const response = await fetch(
        `/api/v1/cell-lines/catalog?${params.toString()}`,
        { credentials: 'include' },
      );
      if (response.status === 401) {
        window.location.assign('/login');
        return;
      }
      if (!response.ok) throw new Error(t('samples.catalogLoadFailed'));
      const body = (await response.json()) as {
        items: CellLineCatalogEntry[];
      };
      setCatalogEntries(body.items);
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('samples.catalogLoadFailed'),
      );
    } finally {
      setCatalogLoading(false);
    }
  };
  const selectCatalogEntry = async (entry: CellLineCatalogEntry) => {
    setCatalogLoading(true);
    try {
      let selectedCellLineId = entry.cellLineId;
      if (!selectedCellLineId) {
        const response = await fetch('/api/v1/cell-lines/catalog/enable', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ catalogIds: [entry.id] }),
        });
        if (!response.ok) throw new Error(t('samples.catalogEnableFailed'));
        const body = (await response.json()) as { created: CellLine[] };
        selectedCellLineId = body.created[0]?.id ?? null;
      }
      if (!selectedCellLineId)
        throw new Error(t('samples.catalogEnableFailed'));
      await load();
      form.setFieldValue('cellLineId', selectedCellLineId);
      form.resetFields(['cellMaterialId']);
      setCatalogOpen(false);
      await messageApi.success(t('samples.catalogSelected'));
    } catch (error) {
      await messageApi.error(
        error instanceof Error
          ? error.message
          : t('samples.catalogEnableFailed'),
      );
    } finally {
      setCatalogLoading(false);
    }
  };
  const openCreateMaterial = () => {
    const cellLineId = form.getFieldValue('cellLineId') as string | undefined;
    if (!cellLineId) {
      void messageApi.error(t('samples.selectCellLineFirst'));
      return;
    }
    materialForm.resetFields();
    materialForm.setFieldsValue({
      cellLineId,
      type: 'TRANSIENT_POOL',
      validationStatus: 'UNVERIFIED',
    });
    setMaterialOpen(true);
  };
  const positionParts = (position: Position) =>
    (position.displayPath ?? position.path)
      .split('/')
      .filter(Boolean)
      .slice(-4);
  const buildPositionCascaderOptions = (source: Position[]) => {
    type Option = { value: string; label: string; children?: Option[] };
    const roots: Option[] = [];
    const getChild = (siblings: Option[], value: string, label = value) => {
      let child = siblings.find((item) => item.value === value);
      if (!child) {
        child = { value, label, children: [] };
        siblings.push(child);
      }
      return child;
    };
    for (const position of source) {
      const parts = positionParts(position);
      let siblings = roots;
      parts.forEach((part, index) => {
        const isLeaf = index === parts.length - 1;
        const value = isLeaf
          ? position.id
          : parts.slice(0, index + 1).join('/');
        const child = getChild(siblings, value, part);
        if (!isLeaf) siblings = child.children ?? [];
        else delete child.children;
      });
    }
    return roots;
  };
  const buildLocationTreeData = (source: Position[] = []) => {
    type Option = {
      title: string;
      value: string;
      key: string;
      pathLabel: string;
      children?: Option[];
    };
    const nodesByPath = new Map(source.map((item) => [item.path, item]));
    const roots: Option[] = [];
    const getChild = (
      siblings: Option[],
      value: string,
      title: string,
      pathLabel: string,
    ) => {
      let child = siblings.find((item) => item.value === value);
      if (!child) {
        child = { title, value, key: value, pathLabel, children: [] };
        siblings.push(child);
      }
      return child;
    };
    for (const location of source) {
      const pathParts = location.path.split('/').filter(Boolean);
      const displayParts = (location.displayPath ?? location.path)
        .split('/')
        .filter(Boolean);
      let siblings = roots;
      pathParts.forEach((part, index) => {
        const path = `/${pathParts.slice(0, index + 1).join('/')}`;
        const node = nodesByPath.get(path);
        const child = getChild(
          siblings,
          node?.id ?? `path:${path}`,
          displayParts[index] ?? part,
          displayParts.slice(0, index + 1).join(' / '),
        );
        if (index < pathParts.length - 1) siblings = child.children ?? [];
      });
    }
    return roots;
  };
  const positionCascaderOptions = useMemo(
    () => buildPositionCascaderOptions(positions),
    [positions],
  );
  const allPositionCascaderOptions = useMemo(
    () => buildPositionCascaderOptions(allPositions),
    [allPositions],
  );
  const storageLocationTreeData = useMemo(
    () => buildLocationTreeData(storageLocations),
    [storageLocations],
  );
  const storageLocationTreeExpandedKeys = useMemo(
    () =>
      storageLocations
        .filter(
          (location) => location.type === 'SITE' || location.type === 'DEVICE',
        )
        .map((location) => location.id),
    [storageLocations],
  );
  const positionCascaderValue = (positionId?: string | null) => {
    const position =
      allPositions.find((item) => item.id === positionId) ??
      positions.find((item) => item.id === positionId);
    if (!position) return undefined;
    const parts = positionParts(position);
    return parts.map((_, index) =>
      index === parts.length - 1
        ? position.id
        : parts.slice(0, index + 1).join('/'),
    );
  };
  const saveCellLine = async (values: CellLineForm) => {
    const response = await fetch('/api/v1/cell-lines', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(values),
    });
    if (!response.ok) {
      await messageApi.error(
        response.status === 409
          ? t('cellLines.duplicate')
          : t('cellLines.saveFailed'),
      );
      return;
    }
    const created = (await response.json()) as CellLine;
    setLines((current) => [created, ...current]);
    form.setFieldValue('cellLineId', created.id);
    setCellLineOpen(false);
    cellLineForm.resetFields();
    await messageApi.success(t('cellLines.saved'));
  };
  const load = async (
    filters: Partial<SampleFilterState> = {},
  ): Promise<SampleListResponse | undefined> => {
    try {
      const next: SampleFilterState = {
        query,
        status,
        storageLocationId,
        cellLineId,
        operatorId,
        cellMaterialId,
        qcStatus,
        freezeDateFrom: freezeDateRange?.[0]?.format('YYYY-MM-DD'),
        freezeDateTo: freezeDateRange?.[1]?.format('YYYY-MM-DD'),
        expiryDateFrom: expiryDateRange?.[0]?.format('YYYY-MM-DD'),
        expiryDateTo: expiryDateRange?.[1]?.format('YYYY-MM-DD'),
        ...filters,
      };
      const params = new URLSearchParams();
      Object.entries(next).forEach(([key, value]) => {
        if (typeof value === 'string' && value.trim())
          params.set(key, value.trim());
      });
      const [
        samples,
        cellLines,
        cellMaterials,
        storage,
        allStorage,
        locationCatalog,
        qcTemplates,
        qcDefinitions,
        qcSettings,
      ] = await Promise.all([
        fetch(`/api/v1/samples?${params}`, { credentials: 'include' }),
        fetch('/api/v1/cell-lines?limit=100', { credentials: 'include' }),
        fetch('/api/v1/cell-materials?limit=200', { credentials: 'include' }),
        fetch('/api/v1/storage-positions', { credentials: 'include' }),
        fetch('/api/v1/storage-positions?availableOnly=false', {
          credentials: 'include',
        }),
        fetch('/api/v1/storage-locations', { credentials: 'include' }),
        fetch('/api/v1/qc-templates', { credentials: 'include' }),
        fetch('/api/v1/qc-definitions', { credentials: 'include' }),
        fetch('/api/v1/qc-settings', { credentials: 'include' }),
      ]);
      if (samples.status === 401) {
        window.location.assign('/login');
        return undefined;
      }
      if (
        !samples.ok ||
        !cellLines.ok ||
        !cellMaterials.ok ||
        !storage.ok ||
        !allStorage.ok ||
        !locationCatalog.ok
      )
        throw new Error();
      const sampleList = (await samples.json()) as SampleListResponse;
      setItems(sampleList.items);
      setStatusCounts(
        sampleList.statusCounts ?? countSampleStatuses(sampleList.items),
      );
      setOperators(sampleList.operators ?? []);
      setLines(((await cellLines.json()) as { items: CellLine[] }).items);
      setMaterials(
        ((await cellMaterials.json()) as { items: CellMaterial[] }).items,
      );
      setPositions(((await storage.json()) as { items: Position[] }).items);
      setAllPositions(
        ((await allStorage.json()) as { items: Position[] }).items,
      );
      const locationData = (await locationCatalog.json()) as
        | Position[]
        | { items?: Position[] };
      setStorageLocations(
        Array.isArray(locationData) ? locationData : (locationData.items ?? []),
      );
      setTemplates(
        qcTemplates.ok
          ? ((await qcTemplates.json()) as { items: QcTemplate[] }).items
          : [],
      );
      setDefinitions(
        qcDefinitions.ok
          ? ((await qcDefinitions.json()) as { items: QcDefinition[] }).items
          : [],
      );
      setTemplatesEnabled(
        qcSettings.ok
          ? ((await qcSettings.json()) as { templatesEnabled: boolean })
              .templatesEnabled
          : false,
      );
      return sampleList;
    } catch {
      await messageApi.error(t('samples.loadFailed'));
      return undefined;
    }
  };
  useEffect(() => {
    void fetch('/api/v1/auth/me', { credentials: 'include' })
      .then(async (response) => {
        if (response.status === 401) {
          window.location.assign('/login');
          return;
        }
        if (response.ok) setCurrentUser((await response.json()) as CurrentUser);
      })
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!open || editing || !selectedCellLineId) return;
    if (form.getFieldValue('cellMaterialId')) return;
    const parentalId = defaultMaterialId(selectedCellLineId);
    if (parentalId) form.setFieldValue('cellMaterialId', parentalId);
  }, [selectedCellLineId, materials, open, editing]);
  const uploadQcAttachment = async (resultId: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(
      `/api/v1/qc-result-sets/${resultId}/attachments`,
      {
        method: 'POST',
        credentials: 'include',
        body,
      },
    );
    if (!response.ok) {
      await messageApi.error(t('quality.uploadFailed'));
      return false;
    }
    return true;
  };
  const save = async (values: SampleForm) => {
    const storagePositionId =
      values.storagePositionPath?.[values.storagePositionPath.length - 1] ??
      editing?.storagePositionId ??
      '';
    const qcTemplate = templates.find(
      (template) => template.id === values.qcTemplateId,
    );
    const qcItems = templatesEnabled
      ? (qcTemplate?.items ?? [])
      : definitions
          .filter((definition) =>
            values.qcDefinitionIds?.includes(definition.id),
          )
          .map((definition) => ({
            ...definition,
            definitionId: definition.id,
          }));
    if (
      !editing &&
      values.enableQc &&
      (!qcItems.length || (templatesEnabled && !qcTemplate))
    ) {
      await messageApi.error(t('quality.selectItems'));
      return;
    }
    const qc =
      !editing && values.enableQc
        ? {
            ...(templatesEnabled ? { templateId: values.qcTemplateId } : {}),
            testedAt: values.qcTestedAt,
            notes: values.qcNotes,
            items: qcItems.map((item) => ({
              definitionId: item.definitionId,
              value: values.values?.[item.definitionId],
            })),
          }
        : undefined;
    const body = {
      sampleNo: editing ? values.sampleNo?.trim() : undefined,
      barcode: values.barcode,
      cellLineId: values.cellLineId,
      cellMaterialId: values.cellMaterialId,
      cellCount: values.cellCount,
      passage: values.passage,
      storagePositionId,
      freezeDate: formatDate(values.freezeDate),
      expiryDate: formatDate(values.expiryDate),
      customFields: {
        ...(editing?.customFields ?? {}),
        label: values.label?.trim() || undefined,
        ownerName: values.ownerName?.trim() || undefined,
        notes: values.notes?.trim() || undefined,
      },
      ...(!editing
        ? {
            putawayReason: values.putawayReason?.trim(),
            putawayRequestTitle: t('samples.putawayRequestTitle', {
              sample:
                values.sampleNo?.trim() ||
                values.label?.trim() ||
                lines.find((line) => line.id === values.cellLineId)?.code ||
                t('requests.genericSamples'),
            }),
          }
        : {}),
      ...(qc ? { qc } : {}),
    };
    const r = await fetch(
      editing ? `/api/v1/samples/${editing.id}` : '/api/v1/samples',
      {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(
          editing ? { ...body, version: editing.version } : body,
        ),
      },
    );
    if (!r.ok) {
      await messageApi.error(t('samples.saveFailed'));
      return;
    }
    const createdSample = (await r.json()) as Sample & {
      putawayRequest?: CreatedRequest;
      putawayTransaction?: Omit<
        Extract<OperationResult, { kind: 'execution' }>,
        'kind'
      >;
    };
    const attachment = values.qcAttachment?.[0]?.originFileObj;
    if (qc && attachment) {
      const resultResponse = await fetch(
        `/api/v1/qc-result-sets?sampleId=${createdSample.id}`,
        { credentials: 'include' },
      );
      if (resultResponse.ok) {
        const result = ((await resultResponse.json()) as { items: QcResult[] })
          .items[0];
        if (result) await uploadQcAttachment(result.id, attachment);
      }
    }
    setOpen(false);
    setEditing(undefined);
    form.resetFields();
    await actionRef.current?.reload();
    if (createdSample.putawayRequest) {
      setRequestOperation('PUTAWAY');
      setRequestSelection([createdSample]);
      setRequestResult({
        kind: 'request',
        request: createdSample.putawayRequest,
      });
      setRequestOpen(true);
      return;
    }
    if (createdSample.putawayTransaction) {
      setRequestOperation('PUTAWAY');
      setRequestSelection([createdSample]);
      setRequestResult({
        kind: 'execution',
        ...createdSample.putawayTransaction,
      });
      setRequestOpen(true);
      return;
    }
    await messageApi.success(t('samples.saved'));
  };
  const openCreate = () => {
    setEditing(undefined);
    form.resetFields();
    form.setFieldsValue({
      enableQc: false,
      qcTestedAt: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };
  useEffect(() => {
    const state = location.state as { createSample?: boolean } | null;
    if (!state?.createSample) return;
    setEditing(undefined);
    form.resetFields();
    form.setFieldsValue({
      enableQc: false,
      qcTestedAt: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
    void navigate('/samples', { replace: true, state: null });
  }, [form, location.state, navigate]);
  const openEdit = (sample: Sample) => {
    setEditing(sample);
    const values: Partial<SampleForm> = {
      sampleNo: sample.sampleNo,
      cellLineId: sample.cellLineId,
    };
    if (sample.cellMaterialId) values.cellMaterialId = sample.cellMaterialId;
    if (sample.cellCount) values.cellCount = sample.cellCount;
    if (sample.passage) values.passage = sample.passage;
    const label = sampleLabel(sample);
    if (label) values.label = label;
    const nextOwnerName = ownerName(sample);
    if (nextOwnerName) values.ownerName = nextOwnerName;
    const notes = sampleNotes(sample);
    if (notes) values.notes = notes;
    if (sample.barcode) values.barcode = sample.barcode;
    const cascaderValue = positionCascaderValue(sample.storagePositionId);
    if (cascaderValue) values.storagePositionPath = cascaderValue;
    const freezeDate = dateValue(sample.freezeDate);
    const expiryDate = dateValue(sample.expiryDate);
    if (freezeDate) values.freezeDate = freezeDate;
    if (expiryDate) values.expiryDate = expiryDate;
    if (!values.cellMaterialId) {
      const materialId = defaultMaterialId(sample.cellLineId);
      if (materialId) values.cellMaterialId = materialId;
    }
    form.setFieldsValue(values);
    setOpen(true);
  };
  const saveMaterial = async (values: CellMaterialForm) => {
    const response = await fetch('/api/v1/cell-materials', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(values),
    });
    if (!response.ok) {
      await messageApi.error(t('samples.materialSaveFailed'));
      return;
    }
    const saved = (await response.json()) as CellMaterial;
    setMaterials((current) => [saved, ...current]);
    form.setFieldValue('cellMaterialId', saved.id);
    setMaterialOpen(false);
    materialForm.resetFields();
    await messageApi.success(t('samples.materialSaved'));
  };
  const buildBulkMoveBody = (values: ActionForm): BulkMoveRequest => ({
    reason: values.reason,
    items: items
      .filter((sample) => selectedIds.includes(sample.id))
      .map((sample) => ({
        sampleId: sample.id,
        version: sample.version,
        toLocationId: bulkMoveTargets[sample.id],
      })),
  });
  const previewBulkMove = async (values: ActionForm) => {
    const body = buildBulkMoveBody(values);
    setPreviewLoading(true);
    try {
      const response = await fetch('/api/v1/samples/bulk-move/preview', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        await messageApi.error(t('samples.previewFailed'));
        return;
      }
      setPendingBulkMove(body);
      setBulkMovePreview((await response.json()) as BulkMovePreview);
      setAction(undefined);
    } finally {
      setPreviewLoading(false);
    }
  };
  const confirmBulkMove = async () => {
    if (!pendingBulkMove || !bulkMovePreview?.executable) return;
    setPreviewLoading(true);
    try {
      const response = await fetch('/api/v1/samples/bulk-move', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(pendingBulkMove),
      });
      if (!response.ok) {
        await messageApi.error(t('samples.actionFailed'));
        return;
      }
      setBulkMovePreview(undefined);
      setPendingBulkMove(undefined);
      setBulkMoveTargets({});
      setSelectedIds([]);
      actionForm.resetFields();
      await actionRef.current?.reload();
      await messageApi.success(t('samples.actionSaved'));
    } finally {
      setPreviewLoading(false);
    }
  };
  const runAction = async (values: ActionForm) => {
    if (!action) return;
    if (action.type === 'bulkMove') {
      await previewBulkMove(values);
      return;
    }
    const actionSample = action.sample;
    if (!actionSample) return;
    const body =
      action.type === 'correct'
        ? {
            version: actionSample.version,
            originalTransactionId: action.transaction?.id,
            status: values.status,
            storagePositionId: values.storagePositionId ?? null,
            reason: values.reason,
          }
        : action.type === 'move'
          ? {
              version: actionSample.version,
              status: actionSample.status,
              storagePositionId: values.toLocationId,
              reason: values.reason,
            }
          : {
              version: actionSample.version,
              status: values.status,
              storagePositionId: values.storagePositionId,
              reason: values.reason,
            };
    const endpoint =
      action.type === 'correct'
        ? `/api/v1/samples/${actionSample.id}/correct`
        : `/api/v1/samples/${actionSample.id}/transition`;
    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await messageApi.error(t('samples.actionFailed'));
      return;
    }
    setAction(undefined);
    setSelectedIds([]);
    actionForm.resetFields();
    await actionRef.current?.reload();
    if (selected?.id === actionSample.id) await inspect(actionSample.id);
    await messageApi.success(t('samples.actionSaved'));
  };
  const runBatchQc = async (values: BatchQcForm) => {
    const template = templates.find((item) => item.id === values.templateId);
    const qcItems = templatesEnabled
      ? (template?.items ?? [])
      : definitions
          .filter((definition) => values.definitionIds?.includes(definition.id))
          .map((definition) => ({
            ...definition,
            definitionId: definition.id,
          }));
    if (!qcItems.length || (templatesEnabled && !template)) {
      await messageApi.error(t('quality.selectItems'));
      return;
    }
    const targetSamples = singleQcSample ? [singleQcSample] : selectedSamples;
    if (!targetSamples.length) return;
    const response = await fetch('/api/v1/qc-result-sets/bulk', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        ...(templatesEnabled
          ? { templateId: values.templateId }
          : { definitionIds: values.definitionIds }),
        testedAt: values.testedAt,
        notes: values.notes,
        samples: targetSamples.map((sample) => ({
          sampleId: sample.id,
          sampleVersion: sample.version,
        })),
        items: qcItems.map((item) => ({
          definitionId: item.definitionId,
          value: values.values?.[item.definitionId],
        })),
      }),
    });
    if (!response.ok) {
      await messageApi.error(t('samples.batchQcFailed'));
      return;
    }
    const created = (await response.json()) as { items: { id: string }[] };
    const attachment = values.attachment?.[0]?.originFileObj;
    if (attachment) {
      for (const result of created.items)
        await uploadQcAttachment(result.id, attachment);
    }
    setBatchQcOpen(false);
    setSingleQcSample(undefined);
    if (!singleQcSample) setSelectedIds([]);
    batchQcForm.resetFields();
    if (singleQcSample) await inspect(singleQcSample.id);
    else await actionRef.current?.reload();
    await messageApi.success(t('samples.batchQcSaved'));
  };
  const exportUrl = (scope: 'selected' | 'all') => {
    const params = new URLSearchParams();
    if (scope === 'selected') {
      if (selectedIds.length) params.set('ids', selectedIds.join(','));
      return `/api/v1/samples/export.csv?${params.toString()}`;
    }
    if (query.trim()) params.set('query', query.trim());
    if (status) params.set('status', status);
    if (storageLocationId) params.set('storageLocationId', storageLocationId);
    if (cellLineId) params.set('cellLineId', cellLineId);
    if (operatorId) params.set('operatorId', operatorId);
    if (cellMaterialId) params.set('cellMaterialId', cellMaterialId);
    if (qcStatus) params.set('qcStatus', qcStatus);
    if (freezeDateRange?.[0])
      params.set('freezeDateFrom', freezeDateRange[0].format('YYYY-MM-DD'));
    if (freezeDateRange?.[1])
      params.set('freezeDateTo', freezeDateRange[1].format('YYYY-MM-DD'));
    if (expiryDateRange?.[0])
      params.set('expiryDateFrom', expiryDateRange[0].format('YYYY-MM-DD'));
    if (expiryDateRange?.[1])
      params.set('expiryDateTo', expiryDateRange[1].format('YYYY-MM-DD'));
    const suffix = params.toString();
    return `/api/v1/samples/export.csv${suffix ? `?${suffix}` : ''}`;
  };
  const precheck = async () => {
    if (!file?.originFileObj) return;
    const data = new FormData();
    data.append('file', file.originFileObj);
    const r = await fetch('/api/v1/imports/sample/precheck', {
      method: 'POST',
      credentials: 'include',
      headers: { 'idempotency-key': crypto.randomUUID() },
      body: data,
    });
    if (!r.ok) {
      await messageApi.error(t('samples.importFailed'));
      return;
    }
    setJob((await r.json()) as ImportJob);
  };
  const execute = async () => {
    if (!job) return;
    const r = await fetch(`/api/v1/imports/${job.id}/execute`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ version: job.version }),
    });
    if (!r.ok) {
      await messageApi.error(t('samples.importFailed'));
      return;
    }
    setJob((await r.json()) as ImportJob);
    setFile(undefined);
    await actionRef.current?.reload();
    await messageApi.success(t('samples.imported'));
  };
  const closeImportModal = () => {
    setImportOpen(false);
    setFile(undefined);
    setJob(undefined);
  };
  const inspect = async (id: string) => {
    setDetailLoading(true);
    setQcResults([]);
    setTransactions([]);
    try {
      const [sample, qc, history] = await Promise.all([
        fetch(`/api/v1/samples/${id}`, { credentials: 'include' }),
        fetch(`/api/v1/qc-result-sets?sampleId=${id}`, {
          credentials: 'include',
        }),
        fetch(`/api/v1/inventory-transactions?sampleId=${id}`, {
          credentials: 'include',
        }),
      ]);
      if (!sample.ok) {
        await messageApi.error(t('samples.detailFailed'));
        return;
      }
      setSelected((await sample.json()) as Sample);
      if (qc.ok)
        setQcResults(((await qc.json()) as { items: QcResult[] }).items);
      if (history.ok)
        setTransactions(
          ((await history.json()) as { items: Transaction[] }).items,
        );
    } finally {
      setDetailLoading(false);
    }
  };
  const name = (line: CellLine) =>
    i18n.resolvedLanguage === 'en-US'
      ? (line.nameEn ?? line.nameZh)
      : (line.nameZh ?? line.nameEn);
  const qcTitle = (item: { nameZh: string | null; nameEn: string | null }) =>
    i18n.resolvedLanguage === 'en-US'
      ? (item.nameEn ?? item.nameZh)
      : (item.nameZh ?? item.nameEn);
  const positionPath = (id: string | null) =>
    id
      ? (allPositions.find((position) => position.id === id)?.displayPath ??
        allPositions.find((position) => position.id === id)?.path ??
        positions.find((position) => position.id === id)?.displayPath ??
        positions.find((position) => position.id === id)?.path ??
        id)
      : '-';
  const positionOptions = positions.map((position) => ({
    value: position.id,
    label: position.displayPath ?? position.path,
  }));
  const allPositionOptions = allPositions.map((position) => ({
    value: position.id,
    label: position.displayPath ?? position.path,
  }));
  const parentPath = (path: string) => {
    const index = path.lastIndexOf('/');
    return index > 0 ? path.slice(0, index) : path;
  };
  const parentDisplayPath = (position: Position) => {
    const label = position.displayPath ?? position.path;
    const parts = label.split('/').filter(Boolean);
    return parts.slice(0, -1).join('/') || label;
  };
  const bulkMoveBoxOptions = useMemo(() => {
    const groups = new Map<string, { label: string; count: number }>();
    for (const position of positions) {
      const key = parentPath(position.path);
      const current = groups.get(key);
      groups.set(key, {
        label: current?.label ?? parentDisplayPath(position),
        count: (current?.count ?? 0) + 1,
      });
    }
    return [...groups.entries()]
      .sort((a, b) => a[1].label.localeCompare(b[1].label))
      .map(([value, group]) => ({
        value,
        label: t('samples.targetBoxOption', {
          box: group.label,
          count: group.count,
        }),
      }));
  }, [positions, t]);
  const issueLabel = (code: string) =>
    t(`samples.bulkMoveIssue.${code}`, { defaultValue: code });
  const statusLabel = (value?: string | null) =>
    value ? t(`samples.statusOptions.${value}`, { defaultValue: value }) : '-';
  const statusOptions = [
    { value: ALL_SAMPLE_STATUSES, label: t('samples.allStatuses') },
    ...SAMPLE_STATUSES.filter((value) => (statusCounts[value] ?? 0) > 0).map(
      (value) => ({
        value,
        label: statusLabel(value),
      }),
    ),
  ];
  const cellLineOptions = lines.map((line) => ({
    value: line.id,
    label: `${line.code}${name(line) ? ` · ${name(line)}` : ''}`,
  }));
  const operatorOptions = operators.map((operator) => ({
    value: operator.id,
    label: operator.username,
  }));
  const materialFilterOptions = materials.map((material) => ({
    value: material.id,
    label: materialLabel(material),
  }));
  const qcStatusOptions = [
    ...new Set(
      items
        .map((item) => item.qcStatus)
        .filter((value): value is string => Boolean(value)),
    ),
  ].map((value) => ({ value, label: value }));
  const changeableStatusOptions = CHANGEABLE_SAMPLE_STATUSES.map((value) => ({
    value,
    label: statusLabel(value),
  }));
  const formatQcValue = (value: unknown) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean')
      return value ? t('quality.pass') : t('quality.fail');
    if (typeof value === 'string' || typeof value === 'number')
      return String(value);
    return JSON.stringify(value);
  };
  const openCorrection = (transaction: Transaction) => {
    if (!selected) return;
    actionForm.resetFields();
    const values: Partial<ActionForm> = { status: selected.status };
    if (selected.storagePositionId)
      values.storagePositionId = selected.storagePositionId;
    actionForm.setFieldsValue(values);
    setAction({ type: 'correct', sample: selected, transaction });
  };
  const qcInput = (item: QcTemplateItem) => {
    const fieldName = ['values', item.definitionId];
    const label = `${qcTitle(item) ?? item.code}${item.unit ? ` (${item.unit})` : ''}`;
    if (item.dataType === 'NUMBER')
      return (
        <Form.Item
          name={fieldName}
          label={label}
          rules={[{ required: item.isRequired }]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      );
    if (item.dataType === 'BOOLEAN')
      return (
        <Form.Item
          name={fieldName}
          label={label}
          rules={[{ required: item.isRequired }]}
        >
          <Select
            options={[
              { value: true, label: t('quality.pass') },
              { value: false, label: t('quality.fail') },
            ]}
          />
        </Form.Item>
      );
    if (item.dataType === 'SELECT')
      return (
        <Form.Item
          name={fieldName}
          label={label}
          rules={[{ required: item.isRequired }]}
        >
          <Select
            options={(item.options ?? []).map((value) => ({
              value,
              label: t(`quality.optionLabels.${item.code}.${value}`, {
                defaultValue: value,
              }),
            }))}
          />
        </Form.Item>
      );
    return (
      <Form.Item
        name={fieldName}
        label={label}
        rules={[{ required: item.isRequired }]}
      >
        <Input />
      </Form.Item>
    );
  };
  const selectedSamples = items.filter((sample) =>
    selectedIds.includes(sample.id),
  );
  const operationSettings =
    currentUser?.tenant.inventoryOperationSettings ??
    DEFAULT_OPERATION_SETTINGS;
  const canPutaway = Boolean(
    canCreateSamples &&
      operationSettings.PUTAWAY?.enabled &&
      (canExecuteInventory || operationSettings.PUTAWAY.userVisible),
  );
  const requestOperationAvailable = (operation: RequestOperation) => {
    const setting = operationSettings[operation];
    if (!setting?.enabled) return false;
    if (canExecuteInventory) return true;
    return Boolean(
      canRequestInventory && operation === 'CONSUME' && setting.userVisible,
    );
  };
  const availableRequestOperations = REQUEST_OPERATIONS.filter(
    requestOperationAvailable,
  );
  const requestExpectedStatuses = expectedStatusesForRequest(requestOperation);
  const requestPreviewRows = requestSelection.map((sample) => ({
    ...sample,
    issue: requestExpectedStatuses.includes(sample.status)
      ? undefined
      : t('requests.localIssue.statusMismatch', {
          expected: requestExpectedStatuses
            .map((value) => statusLabel(value))
            .join(' / '),
        }),
  }));
  const requestBlockerCount = requestPreviewRows.filter(
    (sample) => sample.issue,
  ).length;
  const openRequest = (
    operation: RequestOperation,
    samples: Sample[] = selectedSamples,
  ) => {
    if (!samples.length || !requestOperationAvailable(operation)) return;
    const summary = Array.from(
      new Set(
        samples
          .map((sample) =>
            [
              cellLineCode(sample),
              sample.materialName ??
                materials.find(
                  (material) => material.id === sample.cellMaterialId,
                )?.name,
              sample.passage,
            ]
              .filter(Boolean)
              .join(' · '),
          )
          .filter(Boolean),
      ),
    ).join('、');
    setRequestOperation(operation);
    setRequestSelection([...samples]);
    setRequestResult(undefined);
    requestForm.resetFields();
    requestForm.setFieldsValue({
      title: t('samples.requestDefaultTitle', {
        operation: t(`requests.operationTypes.${operation}`),
        summary: summary || t('requests.genericSamples'),
        count: samples.length,
      }),
      reason: '',
    });
    setRequestOpen(true);
  };
  const closeRequest = () => {
    setRequestOpen(false);
    setRequestResult(undefined);
    setRequestSelection([]);
    requestForm.resetFields();
  };
  const submitRequest = async (values: RequestForm) => {
    setRequestSubmitting(true);
    try {
      if (canExecuteInventory) {
        const executeResponse = await fetch(
          '/api/v1/samples/inventory-actions',
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              operationType: requestOperation,
              reason: values.reason?.trim(),
              items: requestSelection.map((sample) => ({
                sampleId: sample.id,
                version: sample.version,
                ...(['MOVE', 'RETURN'].includes(requestOperation)
                  ? { toLocationId: values.toLocationIds?.[sample.id] }
                  : {}),
              })),
            }),
          },
        );
        if (!executeResponse.ok)
          throw new Error(t('samples.inventoryActionFailed'));
        const executed = (await executeResponse.json()) as Omit<
          Extract<OperationResult, { kind: 'execution' }>,
          'kind'
        >;
        setRequestResult({ kind: 'execution', ...executed });
        setSelectedIds([]);
        await actionRef.current?.reload();
        return;
      }
      const createResponse = await fetch('/api/v1/inventory-requests', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          operationType: requestOperation,
          title: values.title,
          reason: values.reason?.trim() || null,
          items: requestSelection.map((sample) => ({
            sampleId: sample.id,
            expectedSampleVersion: sample.version,
            ...(['MOVE', 'RETURN'].includes(requestOperation)
              ? { toLocationId: values.toLocationIds?.[sample.id] }
              : {}),
          })),
        }),
      });
      if (!createResponse.ok) throw new Error(t('requests.saveFailed'));
      const created = (await createResponse.json()) as CreatedRequest;
      const submitResponse = await fetch(
        `/api/v1/inventory-requests/${created.id}/submit`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ version: created.version }),
        },
      );
      if (!submitResponse.ok) {
        setRequestResult({
          kind: 'request',
          request: created,
          submitFailed: true,
        });
        return;
      }
      const submitted = (await submitResponse.json()) as CreatedRequest;
      setRequestResult({ kind: 'request', request: submitted });
      setSelectedIds([]);
      await actionRef.current?.reload();
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('requests.saveFailed'),
      );
    } finally {
      setRequestSubmitting(false);
    }
  };
  const assignBulkMoveBox = (boxPath: string | undefined) => {
    setBulkMoveBoxPath(boxPath);
    if (!boxPath) {
      setBulkMoveTargets({});
      return;
    }
    const candidates = positions
      .filter((position) => parentPath(position.path) === boxPath)
      .sort((a, b) => a.path.localeCompare(b.path));
    setBulkMoveTargets(
      Object.fromEntries(
        selectedSamples.map((sample, index) => [
          sample.id,
          candidates[index]?.id ?? '',
        ]),
      ),
    );
    if (candidates.length < selectedSamples.length) {
      void messageApi.warning(
        t('samples.targetBoxInsufficient', {
          available: candidates.length,
          selected: selectedSamples.length,
        }),
      );
    }
  };
  const bulkMoveReady =
    action?.type !== 'bulkMove' ||
    (selectedSamples.length > 0 &&
      selectedSamples.every((sample) => Boolean(bulkMoveTargets[sample.id])));
  const displayText = (value: string | null | undefined) =>
    value?.trim() || '-';
  const cellLineCode = (sample: Sample) =>
    lines.find((line) => line.id === sample.cellLineId)?.code ??
    sample.cellLineId;
  const rangeDate = (value: unknown, index: 0 | 1) => {
    if (!Array.isArray(value)) return undefined;
    const part = (value as unknown[])[index];
    if (dayjs.isDayjs(part)) return part.format('YYYY-MM-DD');
    return typeof part === 'string' && part ? part : undefined;
  };
  const dateRangeFromParams = (
    params: Record<string, unknown>,
    rangeKey: string,
    fromKey: string,
    toKey: string,
  ): [Dayjs, Dayjs] | undefined => {
    const range = params[rangeKey];
    if (Array.isArray(range)) {
      const parts = range as unknown[];
      const start = parts[0];
      const end = parts[1];
      if (dayjs.isDayjs(start) && dayjs.isDayjs(end)) return [start, end];
    }
    const from = params[fromKey];
    const to = params[toKey];
    if (typeof from === 'string' && typeof to === 'string' && from && to)
      return [dayjs(from), dayjs(to)];
    return undefined;
  };
  const requestSamples = async (params: Record<string, unknown>) => {
    const nextStatus =
      params.status === ALL_SAMPLE_STATUSES || typeof params.status !== 'string'
        ? undefined
        : params.status;
    const nextFilters: Partial<SampleFilterState> = {
      query: typeof params.query === 'string' ? params.query : '',
      status: nextStatus,
      storageLocationId:
        typeof params.storageLocationId === 'string'
          ? params.storageLocationId
          : typeof params.storageLocationPath === 'string'
            ? params.storageLocationPath
            : lastStringValue(params.storageLocationPath),
      cellLineId:
        typeof params.cellLineId === 'string' ? params.cellLineId : undefined,
      operatorId:
        typeof params.operatorId === 'string' ? params.operatorId : undefined,
      cellMaterialId:
        typeof params.cellMaterialId === 'string'
          ? params.cellMaterialId
          : undefined,
      qcStatus:
        typeof params.qcStatus === 'string' ? params.qcStatus : undefined,
      freezeDateFrom:
        rangeDate(params.freezeDateRange, 0) ??
        (typeof params.freezeDateFrom === 'string'
          ? params.freezeDateFrom
          : undefined),
      freezeDateTo:
        rangeDate(params.freezeDateRange, 1) ??
        (typeof params.freezeDateTo === 'string'
          ? params.freezeDateTo
          : undefined),
      expiryDateFrom:
        rangeDate(params.expiryDateRange, 0) ??
        (typeof params.expiryDateFrom === 'string'
          ? params.expiryDateFrom
          : undefined),
      expiryDateTo:
        rangeDate(params.expiryDateRange, 1) ??
        (typeof params.expiryDateTo === 'string'
          ? params.expiryDateTo
          : undefined),
    };
    setQuery(nextFilters.query ?? '');
    setStatus(nextFilters.status);
    setStorageLocationId(nextFilters.storageLocationId);
    setCellLineId(nextFilters.cellLineId);
    setOperatorId(nextFilters.operatorId);
    setCellMaterialId(nextFilters.cellMaterialId);
    setQcStatus(nextFilters.qcStatus);
    setFreezeDateRange(
      dateRangeFromParams(
        params,
        'freezeDateRange',
        'freezeDateFrom',
        'freezeDateTo',
      ),
    );
    setExpiryDateRange(
      dateRangeFromParams(
        params,
        'expiryDateRange',
        'expiryDateFrom',
        'expiryDateTo',
      ),
    );
    const response = await load(nextFilters);
    return {
      data: response?.items ?? [],
      success: Boolean(response),
      total: response?.items.length ?? 0,
    };
  };
  const applyQueryFilters = (values: SampleQueryForm) => {
    const nextFilters: Partial<SampleFilterState> = {
      query: values.query?.trim() ?? '',
      status: values.status === ALL_SAMPLE_STATUSES ? undefined : values.status,
      storageLocationId: values.storageLocationId,
      cellLineId: values.cellLineId,
      operatorId: values.operatorId,
      cellMaterialId: values.cellMaterialId,
      qcStatus: values.qcStatus,
      freezeDateFrom: rangeDate(values.freezeDateRange, 0),
      freezeDateTo: rangeDate(values.freezeDateRange, 1),
      expiryDateFrom: rangeDate(values.expiryDateRange, 0),
      expiryDateTo: rangeDate(values.expiryDateRange, 1),
    };
    setQuery(nextFilters.query ?? '');
    setStatus(nextFilters.status);
    setStorageLocationId(nextFilters.storageLocationId);
    setCellLineId(nextFilters.cellLineId);
    setOperatorId(nextFilters.operatorId);
    setCellMaterialId(nextFilters.cellMaterialId);
    setQcStatus(nextFilters.qcStatus);
    setActiveFilters(nextFilters);
  };
  const statusValueEnum = Object.fromEntries(
    statusOptions.map((option) => [option.value, { text: option.label }]),
  );
  const proColumns = useMemo<ProColumns<Sample>[]>(
    () => [
      {
        title: t('samples.searchField'),
        dataIndex: 'query',
        key: 'query',
        hideInTable: true,
        order: 10,
        initialValue: query,
        fieldProps: { allowClear: true, placeholder: t('samples.search') },
      },
      {
        title: t('samples.position'),
        dataIndex: 'storagePositionId',
        key: 'storagePositionId',
        width: 220,
        ellipsis: true,
        order: 9,
        valueType: 'select',
        fieldProps: {
          showSearch: true,
          optionFilterProp: 'label',
          options: allPositionOptions,
        },
        render: (_, row) => positionPath(row.storagePositionId),
      },
      {
        title: t('samples.cellLine'),
        dataIndex: 'cellLineId',
        key: 'cellLineId',
        width: 120,
        ellipsis: true,
        order: 8,
        valueType: 'select',
        fieldProps: {
          showSearch: true,
          optionFilterProp: 'label',
          options: cellLineOptions,
        },
        render: (_, row) => cellLineCode(row),
      },
      {
        title: t('samples.operatorFilter'),
        dataIndex: 'operatorId',
        key: 'operatorId',
        hideInTable: true,
        order: 7,
        valueType: 'select',
        fieldProps: {
          showSearch: true,
          optionFilterProp: 'label',
          options: operatorOptions,
        },
      },
      {
        title: t('samples.status'),
        dataIndex: 'status',
        key: 'status',
        width: 90,
        order: 6,
        valueType: 'select',
        valueEnum: statusValueEnum,
        initialValue: status ?? ALL_SAMPLE_STATUSES,
        render: (_, row) => (
          <Tag color={row.status === 'AVAILABLE' ? 'blue' : 'default'}>
            {statusLabel(row.status)}
          </Tag>
        ),
      },
      {
        title: t('samples.material'),
        dataIndex: 'cellMaterialId',
        key: 'cellMaterialId',
        width: 120,
        ellipsis: true,
        order: 5,
        valueType: 'select',
        fieldProps: {
          showSearch: true,
          optionFilterProp: 'label',
          options: materialFilterOptions,
        },
        render: (_, row) => row.materialName ?? '-',
      },
      {
        title: t('samples.qcStatus'),
        dataIndex: 'qcStatus',
        key: 'qcStatus',
        width: 90,
        ellipsis: true,
        order: 4,
        valueType: 'select',
        fieldProps: { allowClear: true, options: qcStatusOptions },
        render: (_, row) => displayText(row.qcStatus),
      },
      {
        title: t('samples.freezeDateRange'),
        dataIndex: 'freezeDateRange',
        key: 'freezeDateRange',
        hideInTable: true,
        order: 3,
        valueType: 'dateRange',
      },
      {
        title: t('samples.expiryDateRange'),
        dataIndex: 'expiryDateRange',
        key: 'expiryDateRange',
        hideInTable: true,
        order: 2,
        valueType: 'dateRange',
      },
      {
        title: t('samples.number'),
        dataIndex: 'sampleNo',
        key: 'sampleNo',
        fixed: 'left',
        width: 150,
        hideInSearch: true,
      },
      {
        title: t('samples.label'),
        dataIndex: 'label',
        key: 'label',
        width: 160,
        ellipsis: true,
        hideInSearch: true,
        render: (_, row) => sampleLabel(row) ?? '-',
      },
      {
        title: t('samples.barcode'),
        dataIndex: 'barcode',
        key: 'barcode',
        width: 100,
        ellipsis: true,
        hideInSearch: true,
        render: (_, row) => displayText(row.barcode),
      },
      {
        title: t('samples.passage'),
        dataIndex: 'passage',
        key: 'passage',
        width: 70,
        hideInSearch: true,
        render: (_, row) => displayText(row.passage),
      },
      {
        title: t('samples.cellCount'),
        dataIndex: 'cellCount',
        key: 'cellCount',
        width: 90,
        hideInSearch: true,
        render: (_, row) => displayText(row.cellCount),
      },
      {
        title: t('samples.ownerName'),
        dataIndex: 'ownerName',
        key: 'ownerName',
        width: 120,
        ellipsis: true,
        hideInSearch: true,
        render: (_, row) => ownerName(row) ?? '-',
      },
      {
        title: t('samples.freezeDate'),
        dataIndex: 'freezeDate',
        key: 'freezeDate',
        width: 120,
        hideInSearch: true,
        render: (_, row) => formatTenantDate(row.freezeDate),
      },
      {
        title: t('samples.expiryDate'),
        dataIndex: 'expiryDate',
        key: 'expiryDate',
        width: 120,
        hideInSearch: true,
        render: (_, row) => formatTenantDate(row.expiryDate),
      },
      {
        title: t('samples.actions'),
        key: 'actions',
        valueType: 'option',
        fixed: 'right',
        width: canEditSamples ? 170 : 120,
        hideInSearch: true,
        render: (_, row) => (
          <Space size="middle" wrap={false}>
            <Typography.Link onClick={() => void inspect(row.id)}>
              {t('samples.detail')}
            </Typography.Link>
            {canEditSamples && (
              <Typography.Link onClick={() => openEdit(row)}>
                {t('samples.edit')}
              </Typography.Link>
            )}
            {(canExecuteInventory || availableRequestOperations.length > 0) && (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    ...availableRequestOperations.map((operation) => ({
                      key: `request:${operation}`,
                      label: t(`requests.operationTypes.${operation}`),
                      danger: operation === 'DISPOSE',
                      disabled: !expectedStatusesForRequest(operation).includes(
                        row.status,
                      ),
                    })),
                    ...(canExecuteInventory &&
                    availableRequestOperations.length > 0
                      ? [{ type: 'divider' as const }]
                      : []),
                    ...(canExecuteInventory
                      ? [
                          {
                            key: 'transition',
                            label: t('samples.changeStatus'),
                          },
                        ]
                      : []),
                  ],
                  onClick: ({ key }) => {
                    if (key.startsWith('request:')) {
                      openRequest(
                        key.slice('request:'.length) as RequestOperation,
                        [row],
                      );
                      return;
                    }
                    setAction({ type: 'transition', sample: row });
                    actionForm.setFieldsValue(
                      row.storagePositionId
                        ? {
                            status: row.status,
                            storagePositionId: row.storagePositionId,
                          }
                        : { status: row.status },
                    );
                  },
                }}
              >
                <Typography.Link onClick={(event) => event.preventDefault()}>
                  <Space size={4}>
                    {t('samples.more')}
                    <MoreOutlined />
                  </Space>
                </Typography.Link>
              </Dropdown>
            )}
          </Space>
        ),
      },
    ],
    [
      actionForm,
      allPositionOptions,
      canEditSamples,
      canExecuteInventory,
      canRequestInventory,
      cellLineOptions,
      currentUser,
      materialFilterOptions,
      operatorOptions,
      qcStatusOptions,
      statusValueEnum,
      t,
    ],
  );
  const overview = (
    <Descriptions bordered column={1}>
      <Descriptions.Item label={t('samples.number')}>
        {selected?.sampleNo ?? '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.label')}>
        {sampleLabel(selected) ?? '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.barcode')}>
        {selected?.barcode ?? '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.cellLine')}>
        {lines.find((line) => line.id === selected?.cellLineId)?.code ??
          selected?.cellLineId}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.material')}>
        {selected?.materialName ?? '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.passage')}>
        {selected?.passage ?? '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.cellCount')}>
        {selected?.cellCount ?? '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.ownerName')}>
        {ownerName(selected) ?? '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.notes')}>
        {sampleNotes(selected) ?? '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.status')}>
        {statusLabel(selected?.status)}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.qcStatus')}>
        {selected?.qcStatus ?? '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.position')}>
        {positionPath(selected?.storagePositionId ?? null)}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.freezeDate')}>
        {formatTenantDate(selected?.freezeDate)}
      </Descriptions.Item>
      <Descriptions.Item label={t('samples.expiryDate')}>
        {formatTenantDate(selected?.expiryDate)}
      </Descriptions.Item>
    </Descriptions>
  );
  const qcTable = (
    <Table
      size="small"
      rowKey="id"
      loading={detailLoading}
      dataSource={qcResults}
      locale={{ emptyText: t('samples.historyUnavailable') }}
      pagination={false}
      expandable={{
        expandedRowRender: (row: QcResult) => (
          <Table
            size="small"
            rowKey="definitionId"
            pagination={false}
            dataSource={row.items}
            columns={[
              { title: t('quality.code'), dataIndex: 'definitionCode' },
              {
                title: t('quality.value'),
                dataIndex: 'value',
                render: (value: unknown) => formatQcValue(value),
              },
              {
                title: t('quality.passed'),
                dataIndex: 'passed',
                render: (value: boolean | null) =>
                  value === null ? (
                    <Tag>{t('quality.notEvaluated')}</Tag>
                  ) : value ? (
                    <Tag color="green">{t('quality.pass')}</Tag>
                  ) : (
                    <Tag color="red">{t('quality.fail')}</Tag>
                  ),
              },
              {
                title: t('quality.abnormalReason'),
                dataIndex: 'abnormalReason',
                render: (value: string | null) => value ?? '-',
              },
            ]}
          />
        ),
        rowExpandable: (row) => row.items.length > 0,
      }}
      columns={[
        ...(templatesEnabled
          ? [{ title: t('quality.template'), dataIndex: 'templateCode' }]
          : []),
        { title: t('samples.conclusion'), dataIndex: 'conclusion' },
        {
          title: t('samples.testedAt'),
          dataIndex: 'testedAt',
          render: (v: string) => formatTenantDateTime(v),
        },
        { title: t('samples.notes'), dataIndex: 'notes' },
        {
          title: t('quality.attachments'),
          render: (_, row: QcResult) =>
            row.attachments.length ? (
              <Space wrap>
                {row.attachments.map((attachment) => (
                  <Button
                    key={attachment.id}
                    type="link"
                    icon={<DownloadOutlined />}
                    href={`/api/v1/attachments/${attachment.id}/download`}
                  >
                    {attachment.originalFilename}
                  </Button>
                ))}
              </Space>
            ) : (
              t('samples.noAttachments')
            ),
        },
      ]}
    />
  );
  const historyTable = (
    <Table
      size="small"
      rowKey="id"
      loading={detailLoading}
      dataSource={transactions}
      locale={{ emptyText: t('samples.historyUnavailable') }}
      pagination={false}
      columns={[
        { title: t('samples.transactionNo'), dataIndex: 'transactionNo' },
        { title: t('samples.operation'), dataIndex: 'operationType' },
        {
          title: t('samples.statusChange'),
          render: (_, row: Transaction) => {
            const item = row.items.find(
              (next) => next.sampleId === selected?.id,
            );
            return `${statusLabel(item?.fromStatus)} → ${statusLabel(item?.toStatus)} `;
          },
        },
        {
          title: t('samples.executedAt'),
          dataIndex: 'executedAt',
          render: (v: string) => formatTenantDateTime(v),
        },
        {
          title: t('samples.actions'),
          render: (_, row: Transaction) => (
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={
                !selected || ['CONSUMED', 'DISPOSED'].includes(selected.status)
              }
              onClick={() => openCorrection(row)}
            >
              {t('samples.correct')}
            </Button>
          ),
        },
      ]}
    />
  );
  return (
    <section className="samples-page">
      {holder}
      <ProCard className="samples-query-card" variant="outlined">
        <QueryFilter<SampleQueryForm>
          formRef={queryFormRef}
          defaultCollapsed
          defaultFormItemsNumber={3}
          span={6}
          searchGutter={12}
          labelWidth="auto"
          initialValues={{
            query,
            operatorId,
            status: status ?? ALL_SAMPLE_STATUSES,
          }}
          collapseRender={(collapsed) =>
            collapsed ? t('samples.moreFilters') : t('samples.collapseFilters')
          }
          onFinish={(values) => {
            applyQueryFilters(values);
          }}
          onReset={() => {
            queryFormRef.current?.resetFields();
            setActiveFilters({ query: '', status: undefined });
          }}
        >
          <ProFormText
            name="query"
            label={t('samples.searchField')}
            width="xs"
            fieldProps={{ placeholder: t('samples.search') }}
          />
          <ProFormTreeSelect
            name="storageLocationId"
            label={t('samples.storageFilter')}
            width="lg"
            colProps={{ span: 8 }}
            fieldProps={{
              treeData: storageLocationTreeData,
              treeDefaultExpandedKeys: storageLocationTreeExpandedKeys,
              popupMatchSelectWidth: 360,
              treeLine: true,
              treeNodeFilterProp: 'title',
              treeNodeLabelProp: 'pathLabel',
              classNames: {
                popup: { root: 'sample-location-tree-select' },
              },
              showSearch: true,
            }}
          />
          <ProFormSelect
            name="cellLineId"
            label={t('samples.cellLine')}
            width="xs"
            options={cellLineOptions}
            fieldProps={{ showSearch: true, optionFilterProp: 'label' }}
          />
          <ProFormSelect
            name="operatorId"
            label={t('samples.operatorFilter')}
            width="xs"
            options={operatorOptions}
            fieldProps={{ showSearch: true, optionFilterProp: 'label' }}
          />
          <ProFormSelect
            name="status"
            label={t('samples.status')}
            width="xs"
            options={statusOptions}
          />
          <ProFormSelect
            name="cellMaterialId"
            label={t('samples.materialFilter')}
            width="xs"
            options={materialFilterOptions}
            fieldProps={{ showSearch: true, optionFilterProp: 'label' }}
          />
          <ProFormSelect
            name="qcStatus"
            label={t('samples.qcStatusFilter')}
            width="xs"
            options={qcStatusOptions}
          />
          <ProFormDateRangePicker
            name="freezeDateRange"
            label={t('samples.freezeDateRange')}
            width="xs"
          />
          <ProFormDateRangePicker
            name="expiryDateRange"
            label={t('samples.expiryDateRange')}
            width="xs"
          />
        </QueryFilter>
      </ProCard>
      <ProTable<Sample>
        className="samples-table-card"
        rowKey="id"
        actionRef={actionRef}
        columns={proColumns}
        params={activeFilters}
        request={requestSamples}
        headerTitle={t('samples.listTitle')}
        search={false}
        cardBordered
        defaultSize="small"
        options={{
          reload: true,
          density: true,
          setting: true,
          fullScreen: true,
        }}
        columnsState={{
          persistenceKey: 'sample-pro-table-columns-v3',
          persistenceType: 'localStorage',
          defaultValue: {
            cellLineId: { show: true, order: 0 },
            cellMaterialId: { show: true, order: 1 },
            passage: { show: true, order: 2 },
            ownerName: { show: true, order: 3 },
            status: { show: true, order: 4 },
            storagePositionId: { show: true, order: 5 },
            label: { show: true, order: 6 },
            freezeDate: { show: true, order: 7 },
            expiryDate: { show: true, order: 8 },
            cellCount: { show: true, order: 9 },
            qcStatus: { show: true, order: 10 },
            sampleNo: { show: true, order: 11 },
            barcode: { show: false, order: 12 },
          },
        }}
        rowSelection={{
          fixed: true,
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys.map(String)),
        }}
        pagination={{ defaultPageSize: 10 }}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
        tableAlertRender={({ selectedRowKeys }) => (
          <Typography.Text type="secondary">
            {t('samples.selectedCount', { count: selectedRowKeys.length })}
          </Typography.Text>
        )}
        tableAlertOptionRender={(props) => (
          <Space wrap>
            {availableRequestOperations.map((operation) => (
              <Button
                key={operation}
                type="link"
                danger={operation === 'DISPOSE'}
                icon={requestOperationIcon(operation)}
                onClick={() => openRequest(operation)}
              >
                {t(`requests.operationTypes.${operation}`)}
              </Button>
            ))}
            {canManageQc && (
              <Button
                type="link"
                icon={<ExperimentOutlined />}
                disabled={
                  templatesEnabled ? !templates.length : !definitions.length
                }
                onClick={() => {
                  batchQcForm.resetFields();
                  batchQcForm.setFieldValue(
                    'testedAt',
                    new Date().toISOString().slice(0, 10),
                  );
                  setBatchQcOpen(true);
                }}
              >
                {t('samples.batchQc')}
              </Button>
            )}
            <Button type="link" onClick={props.onCleanSelected}>
              {t('common.clearSelection')}
            </Button>
          </Space>
        )}
        toolBarRender={() => [
          ...(canPutaway
            ? [
                <Button
                  key="create"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCreate}
                >
                  {t('samples.putaway')}
                </Button>,
              ]
            : []),
          ...(canManageImports
            ? [
                <Button
                  key="import"
                  icon={<UploadOutlined />}
                  onClick={() => setImportOpen(true)}
                >
                  {t('samples.import')}
                </Button>,
                <Dropdown
                  key="export"
                  trigger={['click']}
                  rootClassName="samples-export-dropdown"
                  menu={{
                    items: [
                      {
                        key: 'selected',
                        label: t('samples.exportSelected'),
                        disabled: !selectedIds.length,
                      },
                      {
                        key: 'all',
                        label: t('samples.exportAll'),
                      },
                    ],
                    onClick: ({ key }) => {
                      if (key === 'selected' || key === 'all')
                        window.location.href = exportUrl(key);
                    },
                  }}
                >
                  <Button icon={<DownloadOutlined />}>
                    {t('samples.exportCsv')}
                  </Button>
                </Dropdown>,
              ]
            : []),
        ]}
      />
      <Modal
        open={requestOpen}
        title={t(
          canExecuteInventory
            ? 'samples.executeModalTitle'
            : 'samples.requestModalTitle',
          {
            operation: t(`requests.operationTypes.${requestOperation}`),
          },
        )}
        width={920}
        onCancel={closeRequest}
        onOk={() => void requestForm.submit()}
        okText={t(
          canExecuteInventory
            ? 'samples.confirmExecution'
            : 'requests.submitApplication',
        )}
        cancelText={t('common.cancel')}
        confirmLoading={requestSubmitting}
        okButtonProps={{ disabled: requestBlockerCount > 0 }}
        footer={requestResult ? null : undefined}
        maskClosable={!requestSubmitting}
      >
        {requestResult ? (
          <Result
            status={
              requestResult.kind === 'request' && requestResult.submitFailed
                ? 'warning'
                : 'success'
            }
            title={
              requestResult.kind === 'execution'
                ? t('samples.executionResultTitle')
                : requestResult.submitFailed
                  ? t('requests.submitFailed')
                  : t('samples.requestResultTitle', {
                      status: t(
                        `requests.statusOptions.${requestResult.request.status}`,
                        { defaultValue: requestResult.request.status },
                      ),
                    })
            }
            subTitle={
              requestResult.kind === 'execution'
                ? t('samples.executionResultSubtitle', {
                    transactionNo: requestResult.transactionNo,
                    operation: t(
                      `requests.operationTypes.${requestResult.operationType}`,
                    ),
                    count: requestResult.itemCount,
                  })
                : t(
                    requestResult.submitFailed
                      ? 'samples.requestDraftSubtitle'
                      : 'samples.requestResultSubtitle',
                    {
                      requestNo: requestResult.request.requestNo,
                      operation: t(
                        `requests.operationTypes.${requestResult.request.operationType}`,
                      ),
                    },
                  )
            }
            extra={[
              ...(requestResult.kind === 'request'
                ? [
                    <Button
                      key="view"
                      type="primary"
                      onClick={() => {
                        closeRequest();
                        void navigate('/requests');
                      }}
                    >
                      {t('samples.viewRequest')}
                    </Button>,
                  ]
                : []),
              <Button
                key="close"
                type={
                  requestResult.kind === 'execution' ? 'primary' : 'default'
                }
                onClick={closeRequest}
              >
                {t('common.close')}
              </Button>,
            ]}
          />
        ) : (
          <Form
            form={requestForm}
            layout="vertical"
            onFinish={(values) => void submitRequest(values)}
          >
            <Alert
              type={
                requestBlockerCount
                  ? 'error'
                  : requestOperation === 'DISPOSE'
                    ? 'warning'
                    : 'info'
              }
              showIcon
              message={
                requestBlockerCount
                  ? t('requests.localBlockers', {
                      count: requestBlockerCount,
                    })
                  : requestOperation === 'DISPOSE'
                    ? t(
                        canExecuteInventory
                          ? 'samples.disposeExecutionWarning'
                          : 'samples.disposeRequestWarning',
                        {
                          count: requestSelection.length,
                        },
                      )
                    : canExecuteInventory
                      ? t('samples.executionPreviewHint', {
                          count: requestSelection.length,
                          operation: t(
                            `requests.operationTypes.${requestOperation}`,
                          ),
                        })
                      : t('requests.previewHint', {
                          count: requestSelection.length,
                          operation: t(
                            `requests.operationTypes.${requestOperation}`,
                          ),
                        })
              }
              style={{ marginBottom: 16 }}
            />
            {!canExecuteInventory && (
              <Form.Item
                name="title"
                label={t('requests.title')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            )}
            <Form.Item
              name="reason"
              label={t('requests.reason')}
              {...(canExecuteInventory
                ? {
                    rules: [
                      {
                        required: true,
                        message: t('samples.executionReasonRequired'),
                      },
                    ],
                  }
                : {})}
            >
              <AutoComplete
                allowClear
                options={[
                  { value: t('requests.reasonOptions.experiment') },
                  { value: t('requests.reasonOptions.recovery') },
                  { value: t('requests.reasonOptions.quality') },
                  { value: t('requests.reasonOptions.collaboration') },
                  { value: t('requests.reasonOptions.other') },
                ]}
                placeholder={t('requests.reasonPlaceholder')}
                filterOption={(input, option) =>
                  String(option?.value ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Typography.Title level={5} style={{ margin: '8px 0 12px' }}>
              {t('requests.items')}
            </Typography.Title>
            <Table<(typeof requestPreviewRows)[number]>
              size="small"
              rowKey="id"
              pagination={false}
              tableLayout="fixed"
              scroll={{
                x: ['MOVE', 'RETURN'].includes(requestOperation) ? 1010 : 790,
              }}
              dataSource={requestPreviewRows}
              columns={[
                {
                  title: t('samples.number'),
                  dataIndex: 'sampleNo',
                  width: 145,
                  ellipsis: true,
                },
                {
                  title: t('samples.cellLine'),
                  width: 130,
                  ellipsis: true,
                  render: (_, row) => cellLineCode(row),
                },
                {
                  title: t('samples.material'),
                  width: 145,
                  ellipsis: true,
                  render: (_, row) =>
                    row.materialName ??
                    materials.find(
                      (material) => material.id === row.cellMaterialId,
                    )?.name ??
                    '-',
                },
                {
                  title: t('samples.passage'),
                  dataIndex: 'passage',
                  width: 80,
                  render: (_, row) => row.passage ?? '-',
                },
                {
                  title: t('samples.status'),
                  dataIndex: 'status',
                  width: 100,
                  render: (_, row) => statusLabel(row.status),
                },
                {
                  title: t('samples.position'),
                  dataIndex: 'storagePositionId',
                  width: 190,
                  ellipsis: true,
                  render: (_, row) => positionPath(row.storagePositionId),
                },
                ...(['MOVE', 'RETURN'].includes(requestOperation)
                  ? [
                      {
                        title: t('requests.targetPosition'),
                        width: 220,
                        render: (_: unknown, row: Sample) => (
                          <Form.Item
                            name={['toLocationIds', row.id]}
                            rules={[
                              {
                                required: true,
                                message: t('requests.targetRequired'),
                              },
                              {
                                validator: (_rule, value: unknown) => {
                                  if (!value) return Promise.resolve();
                                  const targets = requestForm.getFieldValue(
                                    'toLocationIds',
                                  ) as Record<string, string> | undefined;
                                  if (
                                    Object.values(targets ?? {}).filter(
                                      (target) => target === value,
                                    ).length > 1
                                  )
                                    return Promise.reject(
                                      new Error(
                                        t('samples.requestDuplicateTarget'),
                                      ),
                                    );
                                  return Promise.resolve();
                                },
                              },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <Select
                              showSearch
                              optionFilterProp="label"
                              placeholder={t('samples.targetPosition')}
                              options={positions.map((position) => ({
                                value: position.id,
                                label: position.displayPath ?? position.path,
                              }))}
                            />
                          </Form.Item>
                        ),
                      },
                    ]
                  : []),
                {
                  title: t('requests.localCheck'),
                  width: 120,
                  render: (_, row) =>
                    row.issue ? (
                      <Tag color="red">{row.issue}</Tag>
                    ) : (
                      <Tag color="green">{t('samples.executable')}</Tag>
                    ),
                },
              ]}
            />
          </Form>
        )}
      </Modal>
      <Drawer
        title={selected?.sampleNo}
        open={!!selected}
        onClose={() => setSelected(undefined)}
        width={720}
        extra={
          canManageQc && selected ? (
            <Button
              type="primary"
              icon={<ExperimentOutlined />}
              disabled={
                templatesEnabled ? !templates.length : !definitions.length
              }
              onClick={() => {
                batchQcForm.resetFields();
                batchQcForm.setFieldValue(
                  'testedAt',
                  new Date().toISOString().slice(0, 10),
                );
                setSingleQcSample(selected);
                setBatchQcOpen(true);
              }}
            >
              {t('samples.recordQc')}
            </Button>
          ) : null
        }
      >
        <Tabs
          items={[
            {
              key: 'overview',
              label: t('samples.overview'),
              children: overview,
            },
            {
              key: 'quality',
              label: t('samples.qualityHistory'),
              children: qcTable,
            },
            {
              key: 'history',
              label: t('samples.inventoryHistory'),
              children: historyTable,
            },
          ]}
        />
      </Drawer>
      <Modal
        open={open}
        title={
          editing
            ? t('samples.editTitle')
            : t(
                canExecuteInventory
                  ? 'samples.createTitleImmediate'
                  : 'samples.createTitleRequest',
              )
        }
        width={720}
        onCancel={() => {
          setOpen(false);
          setEditing(undefined);
        }}
        onOk={() => void form.submit()}
        okText={
          editing
            ? t('common.save')
            : t(
                canExecuteInventory
                  ? 'samples.confirmPutaway'
                  : 'samples.submitPutawayRequest',
              )
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => void save(values)}
        >
          <Row gutter={16}>
            {editing && (
              <Col xs={24} md={12}>
                <Form.Item name="sampleNo" label={t('samples.number')}>
                  <Input readOnly />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} md={12}>
              <Form.Item
                name="cellLineId"
                label={
                  <Space>
                    <span>{t('samples.cellLine')}</span>
                    <Button
                      size="small"
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        cellLineForm.resetFields();
                        cellLineForm.setFieldsValue({
                          originType: 'COMMERCIAL',
                        });
                        setCellLineOpen(true);
                      }}
                    >
                      {t('cellLines.create')}
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      onClick={() => {
                        setCatalogOpen(true);
                        setCatalogQuery('');
                        void loadCatalog('');
                      }}
                    >
                      {t('samples.selectFromCatalog')}
                    </Button>
                  </Space>
                }
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  onChange={(cellLineId: string) => {
                    form.setFieldValue(
                      'cellMaterialId',
                      defaultMaterialId(cellLineId),
                    );
                  }}
                  options={lines.map((line) => ({
                    value: line.id,
                    label:
                      name(line) && name(line) !== line.code
                        ? `${line.code} — ${name(line)}`
                        : line.code,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="cellMaterialId"
                label={
                  <Space>
                    <span>{t('samples.material')}</span>
                    <Button
                      size="small"
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={openCreateMaterial}
                    >
                      {t('samples.createMaterial')}
                    </Button>
                  </Space>
                }
              >
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={materialsForLine(selectedCellLineId).map(
                    (material) => ({
                      value: material.id,
                      label: materialLabel(material),
                    }),
                  )}
                  placeholder={t('samples.parentalMaterial')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="label" label={t('samples.label')}>
                <Input placeholder={t('samples.labelPlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="passage" label={t('samples.passage')}>
                <Input placeholder="P12" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="ownerName"
                label={t('samples.ownerName')}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('storage.entry.validation.required'),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="cellCount"
                label={t('samples.cellCount')}
                tooltip={t('samples.cellCountHint')}
              >
                <Input placeholder="1e6" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="storagePositionPath"
                label={t('samples.position')}
                rules={[{ required: !editing }]}
              >
                <Cascader
                  disabled={!!editing}
                  showSearch
                  options={
                    editing
                      ? allPositionCascaderOptions
                      : positionCascaderOptions
                  }
                  placeholder={t('samples.position')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="freezeDate"
                label={t('samples.freezeDate')}
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="expiryDate" label={t('samples.expiryDate')}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="barcode" label={t('samples.barcode')}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label={t('samples.notes')}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            {!editing && (
              <Col xs={24}>
                <Form.Item
                  name="putawayReason"
                  label={t('requests.reason')}
                  rules={[
                    {
                      required: true,
                      message: t('samples.putawayReasonRequired'),
                    },
                  ]}
                  extra={t(
                    canExecuteInventory
                      ? 'samples.putawayImmediateHint'
                      : 'samples.putawayRequestHint',
                  )}
                >
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            )}
            {!editing && canManageQc && (
              <Col xs={24}>
                <Form.Item name="enableQc" valuePropName="checked">
                  <Checkbox>{t('samples.recordQcOnCreate')}</Checkbox>
                </Form.Item>
                {createQcEnabled && (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      {templatesEnabled ? (
                        <Form.Item
                          name="qcTemplateId"
                          label={t('quality.template')}
                          rules={[{ required: true }]}
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            options={templates.map((template) => ({
                              value: template.id,
                              label: `${template.code} — ${qcTitle(template) ?? ''}`,
                            }))}
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          name="qcDefinitionIds"
                          label={t('quality.definitions')}
                          rules={[{ required: true }]}
                        >
                          <Select
                            mode="multiple"
                            showSearch
                            optionFilterProp="label"
                            options={definitions.map((definition) => ({
                              value: definition.id,
                              label: `${definition.code} — ${qcTitle(definition) ?? ''}`,
                            }))}
                          />
                        </Form.Item>
                      )}
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="qcTestedAt"
                        label={t('quality.testedAt')}
                        rules={[{ required: true }]}
                      >
                        <Input type="date" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>{selectedCreateQcItems.map(qcInput)}</Col>
                    <Col xs={24}>
                      <Form.Item name="qcNotes" label={t('quality.notes')}>
                        <Input.TextArea rows={2} />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item
                        name="qcAttachment"
                        label={t('quality.attachments')}
                        valuePropName="fileList"
                        getValueFromEvent={uploadFileList}
                      >
                        <Upload
                          beforeUpload={() => false}
                          maxCount={1}
                          accept=".pdf,.png,.jpg,.jpeg,.csv,.txt"
                        >
                          <Button icon={<UploadOutlined />}>
                            {t('quality.upload')}
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
      <Modal
        open={catalogOpen}
        title={t('samples.catalogTitle')}
        width={760}
        footer={null}
        onCancel={() => setCatalogOpen(false)}
      >
        <Input.Search
          allowClear
          value={catalogQuery}
          placeholder={t('samples.catalogSearchPlaceholder')}
          onChange={(event) => setCatalogQuery(event.target.value)}
          onSearch={(value) => void loadCatalog(value)}
          style={{ marginBottom: 16 }}
        />
        <Table<CellLineCatalogEntry>
          rowKey="id"
          loading={catalogLoading}
          dataSource={catalogEntries}
          pagination={false}
          scroll={{ y: 360 }}
          columns={[
            {
              title: t('cellLines.code'),
              dataIndex: 'code',
              width: 130,
            },
            {
              title: t('cellLines.name'),
              width: 160,
              render: (_, row) => row.nameZh || row.nameEn || '-',
            },
            {
              title: t('cellLines.species'),
              dataIndex: 'species',
              width: 150,
            },
            {
              title: t('cellLines.catalogRisk'),
              width: 130,
              render: (_, row) => (
                <Tag color={row.riskStatus === 'CLEAR' ? 'green' : 'orange'}>
                  {t(`cellLines.catalogRiskOptions.${row.riskStatus}`)}
                </Tag>
              ),
            },
            {
              title: t('common.actions'),
              width: 150,
              render: (_, row) => (
                <Button
                  type="link"
                  size="small"
                  disabled={catalogLoading}
                  onClick={() => void selectCatalogEntry(row)}
                >
                  {row.enabled
                    ? t('samples.selectCatalogLine')
                    : t('samples.enableCatalogLine')}
                </Button>
              ),
            },
          ]}
          locale={{ emptyText: t('samples.catalogEmpty') }}
        />
      </Modal>
      <Modal
        open={cellLineOpen}
        title={t('cellLines.createTitle')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onCancel={() => setCellLineOpen(false)}
        onOk={() => void cellLineForm.submit()}
      >
        <Form
          form={cellLineForm}
          layout="vertical"
          onFinish={(values) => void saveCellLine(values)}
        >
          <Form.Item
            name="code"
            label={t('cellLines.code')}
            rules={[{ required: true }]}
          >
            <Input placeholder={t('cellLines.codePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="species"
            label={t('cellLines.species')}
            rules={[{ required: true }]}
          >
            <AutoComplete
              options={speciesOptions}
              placeholder={t('cellLines.speciesPlaceholder')}
              filterOption={(inputValue, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="originType"
            label={t('cellLines.originType')}
            initialValue="COMMERCIAL"
            rules={[{ required: true }]}
          >
            <Select options={originTypeOptions} />
          </Form.Item>
          <Form.Item name="source" label={t('cellLines.source')}>
            <Input />
          </Form.Item>
          <Form.Item name="catalogNo" label={t('cellLines.catalogNo')}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label={t('cellLines.description')}>
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={materialOpen}
        title={t('samples.createMaterial')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onCancel={() => {
          setMaterialOpen(false);
          materialForm.resetFields();
        }}
        onOk={() => void materialForm.submit()}
        width={720}
      >
        <Form
          form={materialForm}
          layout="vertical"
          onFinish={(values) => void saveMaterial(values)}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="cellLineId"
                label={t('samples.cellLine')}
                rules={[{ required: true }]}
              >
                <Select
                  disabled
                  options={lines.map((line) => ({
                    value: line.id,
                    label:
                      name(line) && name(line) !== line.code
                        ? `${line.code} — ${name(line)}`
                        : line.code,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="type"
                label={helpLabel(
                  t('samples.materialType'),
                  t('samples.materialTooltips.materialType'),
                )}
                rules={[{ required: true }]}
              >
                <Select options={materialTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={helpLabel(
                  t('samples.materialName'),
                  t('samples.materialTooltips.materialName'),
                )}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="parentMaterialId"
                label={helpLabel(
                  t('samples.parentMaterial'),
                  t('samples.materialTooltips.parentMaterial'),
                )}
              >
                <Select
                  allowClear
                  options={materialsForLine(
                    materialForm.getFieldValue('cellLineId') as
                      | string
                      | undefined,
                  ).map((material) => ({
                    value: material.id,
                    label: materialLabel(material),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="modificationType"
                label={t('samples.modificationType')}
              >
                <Select allowClear options={modificationTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="targetGene"
                label={helpLabel(
                  t('samples.targetGene'),
                  t('samples.materialTooltips.targetGene'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            {selectedMaterialModificationType === 'TAG' ? (
              <>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tagName"
                    label={helpLabel(
                      t('samples.tagName'),
                      t('samples.materialTooltips.tagName'),
                    )}
                    preserve={false}
                  >
                    <Input placeholder="HiBiT" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tagPosition"
                    label={helpLabel(
                      t('samples.tagPosition'),
                      t('samples.materialTooltips.tagPosition'),
                    )}
                    preserve={false}
                  >
                    <Select allowClear options={tagPositionOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="modificationStrategy"
                    label={helpLabel(
                      t('samples.modificationStrategy'),
                      t('samples.materialTooltips.modificationStrategy'),
                    )}
                    preserve={false}
                  >
                    <Select allowClear options={modificationStrategyOptions} />
                  </Form.Item>
                </Col>
              </>
            ) : null}
            <Col xs={24} md={12}>
              <Form.Item
                name="constructName"
                label={helpLabel(
                  t('samples.constructName'),
                  t('samples.materialTooltips.constructName'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="selectionMarker"
                label={helpLabel(
                  t('samples.selectionMarker'),
                  t('samples.materialTooltips.selectionMarker'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="cloneNo"
                label={helpLabel(
                  t('samples.cloneNo'),
                  t('samples.materialTooltips.cloneNo'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="validationStatus"
                label={helpLabel(
                  t('samples.validationStatus'),
                  t('samples.materialTooltips.validationStatus'),
                )}
                rules={[{ required: true }]}
              >
                <Select options={validationStatusOptions} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label={t('samples.notes')}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        open={!!action}
        title={
          action?.type === 'bulkMove'
            ? t('samples.bulkMove')
            : action?.type === 'move'
              ? t('samples.move')
              : action?.type === 'correct'
                ? t('samples.correctTitle')
                : t('samples.changeStatus')
        }
        confirmLoading={previewLoading}
        onCancel={() => {
          setAction(undefined);
          setBulkMoveTargets({});
          setBulkMoveBoxPath(undefined);
        }}
        onOk={() => void actionForm.submit()}
        okButtonProps={{ disabled: !bulkMoveReady }}
      >
        <Form
          form={actionForm}
          layout="vertical"
          onFinish={(values) => void runAction(values)}
        >
          {action?.type === 'correct' && (
            <Alert
              type="info"
              showIcon
              message={t('samples.correctHint', {
                transaction: action.transaction?.transactionNo ?? '-',
              })}
              style={{ marginBottom: 12 }}
            />
          )}
          {(action?.type === 'transition' || action?.type === 'correct') && (
            <>
              <Form.Item
                name="status"
                label={t('samples.status')}
                rules={[{ required: true }]}
              >
                <Select options={changeableStatusOptions} />
              </Form.Item>
              <Form.Item name="storagePositionId" label={t('samples.position')}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  options={allPositionOptions}
                />
              </Form.Item>
            </>
          )}
          {action?.type === 'move' && (
            <Form.Item
              name="toLocationId"
              label={t('samples.targetPosition')}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={positionOptions}
              />
            </Form.Item>
          )}
          {action?.type === 'bulkMove' && (
            <>
              <Form.Item label={t('samples.targetBox')}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('samples.targetBoxPlaceholder')}
                  value={bulkMoveBoxPath}
                  onChange={assignBulkMoveBox}
                  options={bulkMoveBoxOptions}
                />
              </Form.Item>
              {bulkMoveBoxPath && (
                <Alert
                  showIcon
                  type={bulkMoveReady ? 'info' : 'warning'}
                  message={
                    bulkMoveReady
                      ? t('samples.bulkMoveAutoAssigned', {
                          count: selectedSamples.length,
                        })
                      : t('samples.bulkMoveNeedsMorePositions')
                  }
                  style={{ marginBottom: 12 }}
                />
              )}
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={selectedSamples}
                style={{ marginBottom: 16 }}
                columns={[
                  { title: t('samples.number'), dataIndex: 'sampleNo' },
                  {
                    title: t('samples.position'),
                    render: (_, row: Sample) =>
                      positionPath(row.storagePositionId),
                  },
                  {
                    title: t('samples.targetPosition'),
                    render: (_, row: Sample) =>
                      bulkMoveTargets[row.id] ? (
                        positionPath(bulkMoveTargets[row.id] ?? null)
                      ) : (
                        <Tag color="red">{t('samples.unassignedPosition')}</Tag>
                      ),
                  },
                ]}
              />
            </>
          )}
          <Form.Item
            name="reason"
            label={t('samples.reason')}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={!!bulkMovePreview}
        title={t('samples.bulkMovePreview')}
        okText={t('samples.confirmBulkMove')}
        cancelText={t('common.cancel')}
        confirmLoading={previewLoading}
        okButtonProps={{ disabled: !bulkMovePreview?.executable }}
        onCancel={() => {
          setBulkMovePreview(undefined);
          setPendingBulkMove(undefined);
        }}
        onOk={() => void confirmBulkMove()}
        width={820}
      >
        <Alert
          showIcon
          type={bulkMovePreview?.executable ? 'success' : 'error'}
          message={
            bulkMovePreview?.executable
              ? t('samples.bulkMoveReady', { count: bulkMovePreview.itemCount })
              : t('samples.bulkMoveBlocked', {
                  count: bulkMovePreview?.issues.length ?? 0,
                })
          }
          style={{ marginBottom: 12 }}
        />
        <Table
          size="small"
          rowKey={(row, index) => `${row.sampleId ?? 'item'}-${index ?? 0}`}
          pagination={false}
          dataSource={bulkMovePreview?.items ?? []}
          columns={[
            {
              title: t('samples.number'),
              render: (_, row: BulkMovePreviewItem) =>
                row.sampleNo ?? row.sampleId ?? '-',
            },
            {
              title: t('samples.statusChange'),
              render: (_, row: BulkMovePreviewItem) =>
                `${statusLabel(row.fromStatus)} → ${statusLabel(row.toStatus)}`,
            },
            {
              title: t('samples.position'),
              render: (_, row: BulkMovePreviewItem) =>
                `${positionPath(row.fromLocationId)} → ${positionPath(row.toLocationId)}`,
            },
            {
              title: t('samples.blockers'),
              render: (_, row: BulkMovePreviewItem) =>
                row.issues.length ? (
                  <Space wrap>
                    {row.issues.map((issue) => (
                      <Tag color="red" key={issue.code}>
                        {issueLabel(issue.code)}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Tag color="green">{t('samples.executable')}</Tag>
                ),
            },
          ]}
        />
      </Modal>
      {canManageQc && (
        <Modal
          open={batchQcOpen}
          title={singleQcSample ? t('samples.recordQc') : t('samples.batchQc')}
          okText={t('common.save')}
          cancelText={t('common.cancel')}
          onCancel={() => {
            setBatchQcOpen(false);
            setSingleQcSample(undefined);
          }}
          onOk={() => void batchQcForm.submit()}
        >
          <p>
            {singleQcSample
              ? t('samples.singleQcHint', { sample: singleQcSample.sampleNo })
              : t('samples.batchQcHint', { count: selectedSamples.length })}
          </p>
          <Form
            form={batchQcForm}
            layout="vertical"
            onFinish={(values) => void runBatchQc(values)}
          >
            {templatesEnabled ? (
              <Form.Item
                name="templateId"
                label={t('quality.template')}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={templates.map((template) => ({
                    value: template.id,
                    label: `${template.code} — ${qcTitle(template) ?? ''}`,
                  }))}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="definitionIds"
                label={t('quality.definitions')}
                rules={[{ required: true }]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  options={definitions.map((definition) => ({
                    value: definition.id,
                    label: `${definition.code} — ${qcTitle(definition) ?? ''}`,
                  }))}
                />
              </Form.Item>
            )}
            <Form.Item
              name="testedAt"
              label={t('quality.testedAt')}
              rules={[{ required: true }]}
            >
              <Input type="date" />
            </Form.Item>
            {selectedQcItems.map(qcInput)}
            <Form.Item name="notes" label={t('samples.notes')}>
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item
              name="attachment"
              label={t('quality.attachments')}
              valuePropName="fileList"
              getValueFromEvent={uploadFileList}
            >
              <Upload
                beforeUpload={() => false}
                maxCount={1}
                accept=".pdf,.png,.jpg,.jpeg,.csv,.txt"
              >
                <Button icon={<UploadOutlined />}>{t('quality.upload')}</Button>
              </Upload>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {canManageImports && (
        <Modal
          open={importOpen}
          title={t('samples.importTitle')}
          onCancel={closeImportModal}
          width={760}
          footer={
            <Space>
              <Button onClick={closeImportModal}>
                {job?.status === 'COMPLETED'
                  ? t('common.close')
                  : t('common.cancel')}
              </Button>
              {job && job.status !== 'COMPLETED' ? (
                <Button type="primary" onClick={() => void execute()}>
                  {t('samples.executeImport')}
                </Button>
              ) : null}
            </Space>
          }
        >
          <div className="sample-import-upload">
            <Upload.Dragger
              accept=".xlsx,.csv"
              fileList={file ? [file] : []}
              maxCount={1}
              beforeUpload={(next) => {
                setFile(next);
                setJob(undefined);
                return false;
              }}
              onRemove={() => {
                setFile(undefined);
                setJob(undefined);
                return true;
              }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">{t('samples.dragUploadTitle')}</p>
              <p className="ant-upload-hint">{t('samples.dragUploadHint')}</p>
            </Upload.Dragger>
            <Typography.Link
              className="sample-import-template-link"
              href="/api/v1/imports/sample/template"
              download
            >
              {t('samples.template')}
            </Typography.Link>
          </div>
          <Space style={{ marginTop: 16, marginBottom: 16 }} wrap>
            <Button
              type="primary"
              disabled={!file}
              onClick={() => void precheck()}
            >
              {t('samples.precheck')}
            </Button>
          </Space>
          {job && (
            <Alert
              showIcon
              type={
                job.status === 'COMPLETED' && job.errorRows === 0
                  ? 'success'
                  : job.errorRows
                    ? 'warning'
                    : 'info'
              }
              message={
                job.status === 'COMPLETED'
                  ? t('samples.importCompletedSummary')
                  : t('samples.importPrecheckSummary')
              }
              style={{ marginBottom: 12 }}
            />
          )}
          {job?.autoEnableRows ? (
            <Alert
              showIcon
              type="info"
              message={t('samples.importAutoEnableHint', {
                count: job.autoEnableRows,
              })}
              style={{ marginBottom: 12 }}
            />
          ) : null}
          {job && (
            <p>
              {job.importNo ? <Tag>{job.importNo}</Tag> : null}
              {t('samples.importStatus')}:{' '}
              <Tag color={job.status === 'COMPLETED' ? 'green' : 'blue'}>
                {job.status}
              </Tag>
              {job.sourceFormat ? <Tag>{job.sourceFormat}</Tag> : null}
            </p>
          )}
          {job?.originalFilename && (
            <p>
              {t('samples.importFile')}: {job.originalFilename}
            </p>
          )}
          {job && (
            <>
              <p>
                {t('samples.totalRows')}: {job.totalRows}
              </p>
              <p>
                {t('samples.validRows')}: {job.validRows}
              </p>
              <p>
                {t('samples.errorRows')}: {job.errorRows}
              </p>
            </>
          )}
          {job?.executedAt && (
            <p>
              {t('samples.executedAt')}: {formatTenantDateTime(job.executedAt)}
            </p>
          )}
          {job && job.errorRows > 0 && (
            <Button
              href={`/api/v1/imports/${job.id}/errors.csv`}
              icon={<DownloadOutlined />}
              style={{ marginBottom: 12 }}
            >
              {t('samples.downloadErrors')}
            </Button>
          )}
          {job && job.errors.length > 0 && (
            <Table
              size="small"
              rowKey={(row) =>
                `${row.rowNumber}-${row.code}-${row.field ?? ''}`
              }
              dataSource={job.errors}
              pagination={{ defaultPageSize: 5 }}
              columns={[
                { title: t('samples.rowNumber'), dataIndex: 'rowNumber' },
                {
                  title: t('samples.field'),
                  dataIndex: 'field',
                  render: (value: string | null) => value ?? '-',
                },
                { title: t('samples.errorCode'), dataIndex: 'code' },
                { title: t('samples.messageKey'), dataIndex: 'messageKey' },
              ]}
            />
          )}
        </Modal>
      )}
    </section>
  );
}
